/**
 * `WorkflowRunModel.ts` — web port of Unity's `Models/WorkflowModel/WorkflowRunModel.cs`
 * (trimmed first slice).
 *
 * Thin run sequencer owned by the AI orchestrator (Unity parity): starts/stops off
 * `IProjectModel.onSceneStartPlayModeChanged` (the header Play button, autostart and the agent's
 * `start_workflow` / `stop_workflow` all flip that flag — Unity's `toggle_play_mode` does there),
 * tracks current/completed/resume steps, auto-plays a step's on-start script
 * when it becomes current, advances when its completion script finishes, and persists typed
 * per-user progress through the workflow model's DynamoDB façade. It is also the source of truth for durable
 * workflow/step activity boundaries; project and scene lifecycle events do not
 * stand in for actual playback.
 *
 * Trimmed vs Unity (deliberate first slice): no scene-change → step revisit inference (web
 * scenes don't drive step matching yet — `goToStep` is the explicit navigation seam), and no
 * replay-registry integration (the web script runner reports its own lifecycle).
 */

import { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { IConsolePlacementModel } from "@models/xr/IConsolePlacementModel";
import type { IPermissionsModel } from "@models/permissions/IPermissionsModel";
import { isConfirmedNonAuthor } from "@models/permissions/viewerRole";
import type { ISceneModel } from "@models/scene/ISceneModel";
import { cameraTransitionDurationSeconds } from "@models/data/sceneDto";
import {
  setWorkflowPerformanceMonitoring,
  writeSceneNavigationDiagnostic,
} from "@models/project/SceneNavigationDiagnosticLogger";
import type { IWorkflowActivityService } from "./IWorkflowActivityService";
import type { IProjectWorkflowModel } from "./IProjectWorkflowModel";
import type {
  IWorkflowMediaPlaybackHost,
  IWorkflowOutlineHost,
  IWorkflowRunModel,
  WorkflowMediaCommand,
  WorkflowMediaControlResult,
  WorkflowMoveCause,
  WorkflowMover,
  WorkflowOutlineState,
  WorkflowPlaybackMedia,
  WorkflowPlaybackView,
  WorkflowRunStart,
  WorkflowStartOutcome,
  WorkflowStepArrival,
} from "./IWorkflowRunModel";
import {
  WorkflowCompletionConditionType,
  WorkflowDocument,
  WorkflowRunState,
  WorkflowStep,
  findPlayableNavigationTarget,
  findPlayableStep,
  getPlayableGroupSteps,
  getPlayableSteps,
  getStepInstructions,
  getStepNextButtonLabel,
  isAutostartRequested,
  isConsolePlacementRequired,
  isCompletionStep,
  isProgressRemembered,
  resolveUserInputAllowed,
  isMobileKeyboardDisabled,
  isWorkflowPlayable,
  getStepBackButtonLabel,
  parseMediaTimecodeSeconds,
  resolveMediaFit,
  resolveMediaAutoplay,
  resolveMediaLoop,
  resolveMediaType,
  resolveStepCompletionCondition,
  resolveStepView,
  resolveWorkflowSceneReference,
  stepHasPlayableVideo,
  stepOnStartScriptSpeaks,
} from "./WorkflowDocument";
import {
  resolveUserFirstName,
  resolveWorkflowText,
  type WorkflowTextTokenContext,
} from "./workflowTextTokens";
import { WorkflowScriptRunner } from "./WorkflowScriptRunner";

/**
 * How long after the project and its scene stop loading an AUTOSTARTED run
 * waits before it plays. Applies to autostart only — a pressed Preview starts
 * immediately, because by then the learner is looking at the editor.
 *
 * The workflow document arrives EARLY: its fetch is kicked off by
 * `onActiveProjectChanged`, which `loadProjectAsync` emits as soon as the
 * project's rows are in and long before the scene's GLBs have downloaded. So
 * firing on arrival started the run underneath the loading overlay, with the
 * guide narrating a scene the learner could not see yet.
 *
 * Waiting for the load to SETTLE is the fix; the dwell on top of it is so the
 * run does not begin in the same instant the overlay clears. It also
 * comfortably outlasts the hold each overlay puts on its own filled bar before
 * dismissing (200 ms project, 250 ms scene), so the first line is spoken to a
 * learner who is already looking at the scene.
 */
const K_AUTOSTART_SETTLE_DELAY_MS = 2000;

/**
 * Whether a step's on-start script COLLAPSES Spark's composer while it speaks.
 *
 * **OFF, by direction (2026-09-01)** — "disable behavior of collapsing/
 * uncollapsing ai_panel during workflow… If ai_panel is set to be visible for
 * this workflow or step - its always uncollapsed". So `user_input_allowed` is
 * now the WHOLE answer for the composer: a step that opens `ai_input` shows it
 * from the moment the learner arrives, whatever its script or its
 * `completion_condition` are doing.
 *
 * Kept as a flag rather than deleted because the behaviour it gates is a real
 * design position that was asked for once (2026-08-27) and may be again: while
 * the step is doing the talking, a chat box invites the learner to interrupt
 * the lesson they were told to follow. Flipping this to `true` restores it in
 * one line — the latch, its `wait_for` escape and every prompt bullet around it
 * are all still here and still exercised.
 *
 * ⚠ It gates {@link WorkflowPlaybackView.isAiInputHeld} ONLY, never the latch
 * behind it ({@link WorkflowPlaybackView.isOnStartScriptHoldingFloor}). That
 * latch has a second consumer with nothing to do with the composer — the
 * executor reads it to keep the Guide from narrating over the author's own
 * lines — and gating the ARM site would have silently broken it, which is the
 * one way this change could have made playback worse rather than freer.
 */
const K_HOLD_AI_INPUT_DURING_ON_START_SCRIPT = false;

/**
 * How long the presenter stays delegated to Spark after a run ends while the
 * script runner is still putting the scene back (`finishWorkflowPlaybackAsync`)
 * — see {@link WorkflowRunModel.syncPresenterDelegation}.
 *
 * A CEILING, not a delay: the delegation normally ends the moment that cleanup
 * settles, which is a few frames. The cleanup waits on any tool call still in
 * flight, though, and a call that never returns would otherwise leave an author
 * in an editor with every tool gone and nothing on screen saying why. Past this
 * the editor is handed back with a warning; the cleanup still finishes, and the
 * model's playback mutation scope still keeps what it restores out of the
 * database.
 */
const K_PRESENTER_DELEGATION_CLEANUP_CEILING_MS = 5000;

/**
 * The Guide's whole reply when a learner asks what to do on a step that says
 * nothing out loud (user direction, 2026-09-29). A FIXED line because the
 * composed ones explained the mechanism — "this step doesn't have spoken
 * narration" — which is the result's own wording handed back to the learner.
 * `public/AI/roles/guide.md` carries the same sentence for the Unity client,
 * which does not send this bullet; keep the two identical.
 */
const K_SILENT_STEP_REPLY =
  "Please see the instructions for this step, and let me know if you have questions or are ready to move on.";

export interface WorkflowRunActivityDependencies {
  readonly service: IWorkflowActivityService;
  readonly auth: IAuthModel;
}

/**
 * Console placement, for the workflows that ask to be seated in the wearer's
 * real room before they play (`requires_console_placement`).
 *
 * `isAvailable` is a SEPARATE question from the headset probe beside it, and
 * conflating the two wedges the feature: that probe answers
 * `isPresenting || isHeadsetBrowser`, and opening the placement mode in a
 * headset BROWSER with no live session flips a flag no controller is listening
 * to — nothing moves, nothing ends the mode, and the run never starts. This one
 * must mean "an XR session is live right now".
 */
export interface WorkflowConsolePlacementDependencies {
  readonly model: IConsolePlacementModel;
  readonly isAvailable: () => boolean;
}

interface ActiveWorkflowActivity {
  readonly customerId: string;
  readonly userId: string;
  readonly workflowId: string;
  stepName: string | null;
  stepId: string | null;
  isComplete: boolean;
}

export class WorkflowRunModel implements IWorkflowRunModel {
  readonly onRunStateChanged = new Emitter<void>();
  readonly onStepArrived = new Emitter<WorkflowStepArrival>();
  readonly onRunStarted = new Emitter<WorkflowRunStart>();
  readonly onProjectExitRequested = new Emitter<{ projectId: string; workflowName: string }>();

  private m_isRunning = false;
  private m_currentStepName: string | null = null;
  private m_resumePointStepName: string | null = null;
  private readonly m_completedStepNames: string[] = [];
  /**
   * Every step this run has landed on, in visit order — the ground the learner
   * has actually covered, which is what the agent's named jumps are limited to.
   * Separate from {@link m_completedStepNames} because only Next completes a
   * step: Back, a trigger button and an agent jump all move without completing,
   * and a step reached that way has still been SEEN.
   */
  private readonly m_visitedStepNames: string[] = [];
  /**
   * The learner's PATH — the same arrivals as {@link m_visitedStepNames} but
   * ordered, repeated, and REWINDABLE, always ending on the current step.
   *
   * It is a stack because "go back three steps" is a question about moves, not
   * about steps: branching and repeats mean the third step back may be one they
   * have already been on twice, and a set cannot answer that. Every forward
   * arrival pushes; every back-navigation drops what it returned from, which is
   * what stops "back one" twice from ping-ponging between two steps forever.
   *
   * The two lists deliberately disagree after a rewind, and that is the point:
   * this one says where they ARE in the route, the other says what they have
   * SEEN, and only the second may decide where they are allowed to go.
   */
  private readonly m_visitStack: string[] = [];
  private m_activeActivity: ActiveWorkflowActivity | null = null;
  /**
   * Invalidates an in-flight scene activation when the learner stops or moves
   * again before the target scene finishes loading.
   */
  private m_stepActivationGeneration = 0;
  /**
   * The current step's completion gate — what has to happen before its Next
   * button becomes available (`completion_condition`), and whether it has.
   *
   * Per-ARRIVAL, never persisted: the gate belongs to the moment the learner
   * landed on this step, so a resumed run re-arms it on arrival rather than
   * inheriting a satisfied one from a previous session. `generation` is the
   * step-activation generation it was armed with, which is what stops a script
   * finishing for a step the learner has already left from opening the gate on
   * the step they are on now.
   */
  private m_completionGate: {
    readonly stepKey: string;
    readonly generation: number;
    readonly kind: WorkflowCompletionConditionType;
    satisfied: boolean;
  } | null = null;
  private m_completionGateTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Spark's composer is withheld until this step's on-start script has had its
   * say (user direction, 2026-08-27) — non-null while it is being held back.
   *
   * A LATCH, not a live "is a script playing" reading, and the difference is
   * the whole design: it is armed once at arrival and released once, and
   * nothing re-arms it until the next arrival. A live reading would toggle the
   * composer twice on any script that pauses (a `wait_for`, a `do_not_wait`
   * overlap settling), which on a surface the learner may be mid-sentence in is
   * worse than either state on its own. Opening is also the SAFE direction, so
   * a latch that only opens can never strand them.
   *
   * Armed only when the step HAS an on-start script — synchronously, in
   * {@link beginStepActivation}, so the arrival's own emit already carries it
   * and a step that plays nothing never collapses the composer at all. Held
   * across the scene load too: the wait starts at arrival, not when the script
   * starts speaking, or the panel would flash in and out over a scene change.
   *
   * `generation` is the step-activation generation it was armed with — the same
   * guard the completion gate uses, so a script settling for a step the learner
   * has already left cannot open the composer on the step they are on now.
   */
  private m_aiInputHold: { readonly generation: number } | null = null;
  /** See {@link IWorkflowRunModel.isSceneHidden}. */
  private m_isSceneHidden = false;
  /**
   * The scene whose load session has to settle before the hidden scene may be
   * shown again — set when the scene a step (or the stop) switched to is still
   * being built, so the learner sees it complete rather than assembling.
   *
   * Keyed by the step-activation generation, so a reveal queued for a step the
   * learner has already left — or for a run that has since been replaced —
   * does nothing: every arrival claims a new generation.
   */
  private m_pendingSceneReveal: { readonly sceneId: string; readonly generation: number } | null =
    null;
  /**
   * The mounted media panel's playback controls, or null when none is mounted
   * (no run, a step with no media, or the instant between the 2D panel and the
   * XR slate swapping). One registrant at a time — see
   * {@link IWorkflowRunModel.attachMediaPlaybackHost} for why the release is
   * identity-guarded.
   */
  private m_mediaPlaybackHost: IWorkflowMediaPlaybackHost | null = null;
  /** The workflow outline panel's toggle — see {@link IWorkflowOutlineHost}. */
  private m_outlineHost: IWorkflowOutlineHost | null = null;
  private m_persistedState: WorkflowRunState | null = null;
  private m_loadedStateKey: string | null = null;
  private m_stateLoadKey: string | null = null;
  private m_stateLoadPromise: Promise<void> | null = null;
  private m_stateGeneration = 0;
  private m_startGeneration = 0;
  /**
   * The start that play mode turning ON kicked off, while it is still settling
   * — {@link startOrDropPlayMode}'s promise, null once it has. Kept so
   * {@link requestStartAsync} can WAIT for the start it asked for rather than
   * report a Play press it cannot see the end of, and so the presenter stays
   * delegated while a start is still unwinding ({@link syncPresenterDelegation}).
   */
  private m_pendingPlayModeStart: Promise<void> | null = null;
  /**
   * The release for this model's hold on `IPermissionsModel`'s presenter
   * delegation, or null while it holds none — see
   * {@link syncPresenterDelegation}.
   */
  private m_releasePresenterDelegation: (() => void) | null = null;
  /**
   * The script runner's end-of-playback cleanup that the delegation is waiting
   * out, or null when there is none — see {@link finishPlaybackKeepingDelegation}.
   * Identity-guarded: a newer run's cleanup replaces an older one.
   */
  private m_playbackCleanup: Promise<void> | null = null;
  private m_playbackCleanupCeiling: ReturnType<typeof setTimeout> | null = null;
  /**
   * The document waiting to autostart once loading settles, and the dwell
   * timer that will start it — see {@link maybeAutostart} and
   * {@link K_AUTOSTART_SETTLE_DELAY_MS}. Non-null only between the document
   * arriving and the run actually starting (or the request being dropped).
   */
  private m_pendingAutostartDocument: WorkflowDocument | null = null;
  private m_autostartTimer: ReturnType<typeof setTimeout> | null = null;
  /** A project load is in flight (`onBeginLoadProject` → completed/failed). */
  private m_isProjectLoading = false;
  /**
   * The scene whose load session is open, or null when none is. Tracked by id
   * rather than as a counter because at most one session exists at a time and
   * a new one ABANDONS its predecessor first — so the terminal event names the
   * outgoing scene and lands before the incoming `begin`, and a stale terminal
   * event can never clear a session it does not belong to.
   */
  private m_loadingSceneId: string | null = null;
  /**
   * A start parked on console placement finishing (`requires_console_placement`
   * — see {@link beginConsolePlacementIfRequired}). Non-null only between the
   * mode opening and it closing, and it remembers whether WE opened it: a mode
   * the wearer opened for their own reasons is theirs to close, so tearing the
   * run down must not take it away from them.
   */
  private m_consolePlacementWait: { readonly weOpenedIt: boolean } | null = null;
  /**
   * This start attempt has already been through placement. Without it the
   * re-entered `tryStart` would open the mode again and loop forever. Cleared
   * whenever the run is torn down, so the NEXT run asks again — the flag says
   * the workflow requires placement, and a fresh run is a fresh seating.
   */
  private m_consolePlacementDone = false;

  private readonly m_offFns: Array<() => void> = [];

  /**
   * @param m_isHeadsetPresentation Which autostart flag applies — see
   * {@link maybeAutostart}. Injected rather than resolved: `src/models/**` must
   * not import `src/runtime/**`, and the answer is a runtime/XR fact.
   * @param m_consolePlacement Console placement for
   *   `requires_console_placement`; absent = the client cannot place, so those
   *   workflows start straight away.
   */
  constructor(
    private readonly m_workflowModel: IProjectWorkflowModel,
    private readonly m_project: IProjectModel,
    private readonly m_scriptRunner: WorkflowScriptRunner,
    private readonly m_isHeadsetPresentation: () => boolean = () => false,
    private readonly m_activity?: WorkflowRunActivityDependencies,
    private readonly m_consolePlacement?: WorkflowConsolePlacementDependencies,
    /**
     * The scene model, for a step's `step_view` camera framing. Resolved LAZILY
     * per call for the same reason as the probes above: the viewport mounts and
     * unmounts under an orchestrator that outlives both, and the scene model
     * every method here needs is the one registered now.
     *
     * `null` in a headless run, where the step view is simply not applied —
     * `applyTransientCameraView` answers the same way when no viewport has
     * attached a camera.
     */
    private readonly m_resolveSceneModel: () => ISceneModel | null = () => null,
    /**
     * Permissions, for the VIEWER rules below — a learner who cannot author
     * this project autostarts the workflow whatever the author's flags say,
     * and leaves the project when it finishes.
     *
     * Lazy like the probes above, and for the sharper reason: the model is
     * registered in the CORE pass but its transport (auth / multiuser /
     * project) is attached much further down `registrations.ts`, so a
     * reference captured at construction would be one whose predicates all
     * answer from the seeded least-privilege defaults.
     *
     * `null` in a headless run, where nobody is signed in and no viewer rule
     * can apply — see {@link isViewerLearner}, which fails CLOSED.
     */
    private readonly m_resolvePermissions: () => IPermissionsModel | null = () => null,
  ) {
    this.m_offFns.push(
      this.m_project.onSceneStartPlayModeChanged.on((enabled) => {
        setWorkflowPerformanceMonitoring(enabled);
        writeSceneNavigationDiagnostic("WorkflowRunModel", "play mode changed", {
          enabled,
          isRunning: this.m_isRunning,
          currentStepName: this.m_currentStepName,
          activeSceneId: this.m_project.activeSceneId,
        });
        // FIRST, and synchronously with the press: the user hands the presenter
        // role to Spark the instant play mode turns on, not when the run has
        // finished loading their progress — that gap is where the editor used
        // to stay live (AI-1426).
        this.syncPresenterDelegation();
        if (enabled) {
          this.startOrDropPlayMode();
        } else {
          this.stop();
        }
        this.syncPresenterDelegation();
      }),
      this.m_project.onActiveSceneChanged.on((scene) => {
        writeSceneNavigationDiagnostic("WorkflowRunModel", "observed active scene change", {
          sceneId: scene?.id ?? null,
          sceneName: scene?.name ?? null,
          isRunning: this.m_isRunning,
          currentStepName: this.m_currentStepName,
          activationGeneration: this.m_stepActivationGeneration,
          playMode: this.m_project.isSceneStartPlayModeEnabled,
        });
      }),
      this.m_workflowModel.onWorkflowChanged.on((e) => {
        writeSceneNavigationDiagnostic("WorkflowRunModel", "workflow document changed", {
          origin: e.origin,
          hasDocument: e.document !== null,
          isRunning: this.m_isRunning,
          currentStepName: this.m_currentStepName,
          activeSceneId: this.m_project.activeSceneId,
        });
        if (e.document === null) {
          this.resetRunState();
          this.clearLoadedUserState();
          return;
        }
        if (e.origin === "import") {
          // A replacement/reset is a new authored baseline. Clear this user's
          // progress; legacy shared YAML state is never adopted.
          this.resetRunState();
          this.clearLoadedUserState();
          this.persistWorkflowState();
          return;
        }
        // `load` is the document ARRIVING for a project — the one moment per
        // project open where both the flags and the steps are known. Any other
        // origin is an edit to a project already open, where auto-playing would
        // yank the author out of the editor mid-keystroke.
        if (e.origin === "load") {
          const document = e.document;
          void this.prepareUserStateAsync(document)
            .catch((error) => {
              console.warn("[WorkflowRunModel] Failed to preload workflow user state:", error);
            })
            .finally(() => {
              if (this.m_workflowModel.loadedWorkflow === document) {
                this.maybeAutostart(document);
              }
            });
        }
      }),
    );
    // Load-lifecycle tracking for {@link maybeAutostart}. Subscribed here
    // rather than read on demand because there is no public "is loading" state
    // to read — and it is safe to rely on the events because this model is
    // constructed during app bootstrap, before any project can have started
    // loading, so a `begin` can never have been missed.
    this.m_offFns.push(
      this.m_project.onBeginLoadProject.on(() => {
        this.m_isProjectLoading = true;
        this.reconsiderPendingAutostart();
      }),
      this.m_project.onLoadProjectCompleted.on(() => {
        this.m_isProjectLoading = false;
        this.reconsiderPendingAutostart();
      }),
      this.m_project.onLoadProjectFailed.on(() => {
        // Treated as "loading stopped", not as a reason to start: the dwell
        // re-check re-reads the loaded document, and a failed load has already
        // unloaded the project, so the request drops itself.
        this.m_isProjectLoading = false;
        this.reconsiderPendingAutostart();
      }),
      this.m_project.onBeginLoadScene.on(({ sceneId }) => {
        this.m_loadingSceneId = sceneId;
        this.reconsiderPendingAutostart();
      }),
      this.m_project.onLoadSceneCompleted.on(({ sceneId }) => this.settleSceneLoad(sceneId)),
      this.m_project.onLoadSceneFailed.on(({ sceneId }) => this.settleSceneLoad(sceneId)),
      this.m_project.onLoadSceneAbandoned.on(({ sceneId }) => this.settleSceneLoad(sceneId)),
    );
    /*
      ⚠ THE ESCAPE that keeps the withheld composer from being a dead end.

      A script step of `wait_for` with `trigger: user_response` parks the whole
      lesson on the learner's next turn — and with Spark's composer collapsed
      there is no surface to give one on: `AiPanel` is unmounted, so its input,
      its push-to-talk key and the phone's constant listening all go with it.
      The script would then sit there until its five-minute ceiling elapsed.

      So the wait itself hands the composer back: the hold means "the lesson is
      talking", and a `wait_for` is the lesson saying it has stopped. It reads
      as the feature working rather than as an exception — the composer appears
      exactly when it is the learner's turn.

      Nothing re-takes it when the wait ends (the latch is one-way), which is
      deliberate: re-hiding a box they may be mid-sentence in is worse than
      leaving it up for the rest of the step.

      Guarded on the STEP as well as the generation. A stale script for a step
      of the same name — a revisit whose predecessor is still unwinding — could
      release the current arrival's hold, but releasing is the safe direction,
      so that is a shown composer rather than a hidden one.
    */
    this.m_offFns.push(
      this.m_scriptRunner.onScriptStateChanged.on((state) => {
        if (!state.isAwaitingUserResponse) return;
        if ((this.m_currentStepName ?? "").toLowerCase() !== state.stepName.toLowerCase()) return;
        this.releaseAiInputHold(this.m_stepActivationGeneration);
      }),
    );
    const placement = this.m_consolePlacement;
    if (placement) {
      this.m_offFns.push(
        placement.model.onPlacementModeChanged.on((active) => {
          // Both ways out of the mode look identical here — a pinch at the
          // floor and a Cancel both end it — and BOTH start the run. Cancel
          // means "I am keeping the stage where it was", not "forget the
          // workflow": refusing to start would answer a pressed Play with
          // nothing at all, which is strictly worse than starting on a stage
          // the wearer chose to leave alone.
          if (active || this.m_consolePlacementWait === null) return;
          this.m_consolePlacementWait = null;
          this.m_consolePlacementDone = true;
          // Emitted here rather than left to the start below: a start that
          // REFUSES emits nothing, and the slate would keep saying a run was
          // waiting for it.
          this.onRunStateChanged.emit();
          // Play mode can have been dropped while they were placing (Stop, a
          // project switch, leaving the session). Nothing to resume then.
          if (this.m_isRunning || !this.m_project.isSceneStartPlayModeEnabled) return;
          this.startOrDropPlayMode();
        }),
      );
    }
    const auth = this.m_activity?.auth;
    if (auth) {
      this.m_offFns.push(
        auth.onSignedOut.on(() => {
          this.resetRunState();
          this.clearLoadedUserState();
        }),
        auth.onSignedIn.on(() => {
          const document = this.m_workflowModel.loadedWorkflow;
          if (!document) return;
          void this.prepareUserStateAsync(document).catch((error) => {
            console.warn("[WorkflowRunModel] Failed to preload workflow user state:", error);
          });
        }),
      );
    }
  }

  /**
   * Records that the author asked this client to start the run by itself
   * (`autostart_desktop` / `autostart_headset`, both absent = false), and lets
   * {@link reconsiderPendingAutostart} decide WHEN.
   *
   * It does not start anything here: the document arrives while the project's
   * scene is usually still downloading, so an immediate start plays the first
   * step under the loading overlay. The request is parked instead and fires
   * {@link K_AUTOSTART_SETTLE_DELAY_MS} after the last project/scene load
   * settles.
   *
   * The start itself goes through `isSceneStartPlayModeEnabled` rather than
   * calling `tryStart` directly, so an autostarted run is INDISTINGUISHABLE
   * from a Preview press: the same play-mode chrome hides, the same tools
   * disable, and Stop tears it down the same way. `tryStart` still owns the
   * "is it playable" refusal, and the flag is dropped again when it refuses so
   * play mode never sticks on with no run behind it — the same pairing
   * `useWorkflow.preview` uses.
   */
  private maybeAutostart(document: WorkflowDocument): void {
    if (this.m_isRunning || this.m_project.isSceneStartPlayModeEnabled) return;

    // ⚠ PARKED UNCONDITIONALLY, and the decision is deferred to
    // {@link isAutostartWanted} at fire time. Answering it HERE — which is what
    // this did while the author's flags were the only input — cannot work now
    // that a viewer autostarts regardless of them: the role that makes someone
    // a viewer is resolved asynchronously off the same `onActiveProjectChanged`
    // that fetches this document, so at this instant it is routinely still
    // Reader-by-default and a wrong answer either way is permanent (nothing
    // re-asks). Parking costs a held reference and one dwell timer per project
    // open; the settle window is what buys the role time to land.
    this.m_pendingAutostartDocument = document;
    this.reconsiderPendingAutostart();
  }

  /**
   * Whether this client should start the run by itself, re-read at the moment
   * it would.
   *
   * Two independent reasons, and the viewer one deliberately OVERRIDES the
   * author's flags rather than being ANDed with them: for a learner who cannot
   * author the project, the project IS the workflow, so an explicit
   * `autostart_desktop: false` — which is an authoring convenience, keeping the
   * author's own project openable in the editor — must not strand them in an
   * editor they cannot use, waiting for a Play button they were not told about.
   *
   * Whether the workflow CAN play is not asked here and never is: the flag this
   * returns to only turns play mode ON, and `tryStart` owns the refusal (and
   * `startOrDropPlayMode` drops the flag again when it refuses). Restating the
   * playability predicate is exactly how the Preview button and the run drift
   * apart.
   */
  private isAutostartWanted(document: WorkflowDocument): boolean {
    if (this.isViewerLearner()) return true;

    let isHeadset = false;
    try {
      isHeadset = this.m_isHeadsetPresentation();
    } catch (e) {
      // A missing/failed XR probe must not stop a desktop workflow autostarting.
      console.warn("[WorkflowRunModel] headset probe failed; assuming desktop:", e);
    }
    return isAutostartRequested(document, isHeadset);
  }

  /**
   * True only when we KNOW this user cannot author the open project — the rule
   * is `isConfirmedNonAuthor`, which is shared with the panel-layout lock and
   * carries the reasoning (why the role ALONE, why `"loaded"` is required, and
   * the residual window).
   *
   * It FAILS CLOSED, and here that is what protects the author: not
   * autostarting for a viewer costs them one Play press, while autostarting for
   * an author drops them into playback AND — with the completion rule below —
   * ejects them from their own project when it ends.
   *
   * The `try` is this caller's own: the resolver reaches into the IOC container
   * and a container built by a harness may not carry the token.
   */
  private isViewerLearner(): boolean {
    let permissions: IPermissionsModel | null = null;
    try {
      permissions = this.m_resolvePermissions();
    } catch (e) {
      console.warn("[WorkflowRunModel] permissions unavailable; not treating as a viewer:", e);
      return false;
    }
    return isConfirmedNonAuthor(permissions);
  }

  /**
   * True while a project load or a scene-load session is open.
   *
   * The SESSION, not the chrome: the project-open scene load deliberately runs
   * with `showProgressUi: false` (the project overlay covers it), so gating on
   * what is on screen would read that phase — the long one, where the GLBs
   * download — as quiet.
   */
  private get isProjectOrSceneLoading(): boolean {
    return this.m_isProjectLoading || this.m_loadingSceneId !== null;
  }

  /**
   * Closes the tracked scene-load session when the terminal event belongs to
   * it. Scoped by id so a late event from a superseded session cannot clear
   * the one that replaced it.
   */
  private settleSceneLoad(sceneId: string): void {
    if (this.m_loadingSceneId !== sceneId) return;
    this.m_loadingSceneId = null;
    // ANY terminal outcome shows the scene, not only a completed one: a load
    // that failed or was abandoned will never report "built", and a viewport
    // left empty with nothing to end it is the one outcome this must not have.
    const reveal = this.m_pendingSceneReveal;
    if (reveal?.sceneId === sceneId) {
      this.m_pendingSceneReveal = null;
      if (reveal.generation === this.m_stepActivationGeneration) this.showHiddenScene();
    }
    this.reconsiderPendingAutostart();
  }

  /**
   * Re-arms (or holds back) a parked autostart as loading starts and stops.
   *
   * The dwell restarts from zero every time a load opens, so a scene switch
   * arriving during the wait pushes the start out rather than letting it fire
   * into a second loading screen. Idempotent while quiet — an already-armed
   * timer is left alone, or a burst of progress-adjacent events would keep
   * resetting a countdown that should be running down.
   */
  private reconsiderPendingAutostart(): void {
    if (this.m_pendingAutostartDocument === null) return;

    if (this.isProjectOrSceneLoading) {
      this.clearAutostartTimer();
      return;
    }
    if (this.m_autostartTimer !== null) return;

    /*
      ⚠ A VIEWER GETS NO DWELL, and fires SYNCHRONOUSLY here rather than on a
      0 ms timer.

      The dwell exists so an autostart does not begin under the loading
      overlay — a politeness that assumes the editor is where the user belongs
      and the run is an interruption. For a viewer it is the other way round:
      the run IS the destination, so those two seconds are spent looking at an
      editor they cannot use, which is what "after project loaded - i briefly
      see project ui" was.

      Synchronous matters. This runs inside the `onLoadProjectCompleted`
      dispatch, and `LoadProjectProgressView` handles the same event by holding
      its filled bar for 200 ms before dismissing — so play mode is on, and the
      editor chrome gone, while the overlay is still covering the screen. A
      timer of any length would land after that dispatch and risk the frame the
      overlay uncovers. The cost is that the guide's first line can start during
      the overlay's 200 ms tail; that is the trade, and it is the right way
      round.
    */
    if (this.isViewerLearner()) {
      this.firePendingAutostart();
      return;
    }

    this.m_autostartTimer = setTimeout(() => {
      this.m_autostartTimer = null;
      this.firePendingAutostart();
    }, K_AUTOSTART_SETTLE_DELAY_MS);
  }

  /**
   * Starts the parked run, if everything that made it a good idea still holds.
   *
   * Every guard here is re-read rather than trusted from arm time: two seconds
   * is long enough for the learner to press Preview themselves, for another
   * load to open, or for the project to have been left entirely — and the
   * document identity check is what catches the last one, since
   * `loadedWorkflow` only answers for the project that is still open.
   */
  private firePendingAutostart(): void {
    const document = this.m_pendingAutostartDocument;
    if (document === null) return;
    if (this.isProjectOrSceneLoading) {
      // A load opened between the timer firing and this tick; wait it out.
      this.reconsiderPendingAutostart();
      return;
    }

    this.m_pendingAutostartDocument = null;
    if (this.m_isRunning || this.m_project.isSceneStartPlayModeEnabled) return;
    if (this.m_workflowModel.loadedWorkflow !== document) return;
    // Asked HERE and nowhere earlier — see {@link maybeAutostart}.
    if (!this.isAutostartWanted(document)) return;

    this.m_project.isSceneStartPlayModeEnabled = true;
  }

  private clearAutostartTimer(): void {
    if (this.m_autostartTimer === null) return;
    clearTimeout(this.m_autostartTimer);
    this.m_autostartTimer = null;
  }

  /** Drops a parked autostart — the document it belonged to is gone. */
  private cancelPendingAutostart(): void {
    this.clearAutostartTimer();
    this.m_pendingAutostartDocument = null;
  }

  /**
   * Runs a start and drops play mode again if it refuses, so the flag never
   * sticks on with no run behind it.
   *
   * One copy because there are now two entry points — play mode turning on,
   * and console placement finishing — and a second hand-written pairing is how
   * one of them ends up leaving the chrome hidden over an editor.
   */
  private startOrDropPlayMode(): void {
    const start: Promise<void> = this.tryStart()
      .then((name) => {
        if (name === null && this.m_project.isSceneStartPlayModeEnabled) {
          this.m_project.isSceneStartPlayModeEnabled = false;
        }
      })
      .catch((error) => {
        console.warn("[WorkflowRunModel] Failed to load workflow user state:", error);
        if (this.m_project.isSceneStartPlayModeEnabled) {
          this.m_project.isSceneStartPlayModeEnabled = false;
        }
      })
      .finally(() => {
        // Identity-guarded: a newer start may already have replaced this one.
        if (this.m_pendingPlayModeStart === start) this.m_pendingPlayModeStart = null;
        this.syncPresenterDelegation();
      });
    this.m_pendingPlayModeStart = start;
  }

  /**
   * Holds `IPermissionsModel`'s presenter delegation for exactly as long as a
   * run is LIVE — the user has handed the presenter role to Spark, so they
   * have no scene capability of their own (AI-1426). Idempotent; called
   * wherever one of its inputs changes.
   *
   * Live is WIDER than {@link isRunning} at both ends, and each extra term is a
   * window that used to leave the editor usable:
   *   - play mode is on — from the Preview press, autostart or `start_workflow`,
   *     while {@link tryStart} is still reading the user's saved progress;
   *   - a start is still unwinding ({@link m_pendingPlayModeStart}), e.g. a Stop
   *     pressed inside that window;
   *   - a start is parked on console placement;
   *   - the run has ended but the script runner is still putting the scene back
   *     ({@link m_playbackCleanup}), bounded by
   *     {@link K_PRESENTER_DELEGATION_CLEANUP_CEILING_MS}.
   *
   * A headless host has no permissions model, and then there is nothing to
   * delegate: the run plays the same, with no editor to lock.
   */
  private syncPresenterDelegation(): void {
    const isLive =
      this.m_project.isSceneStartPlayModeEnabled
      || this.m_isRunning
      || this.m_pendingPlayModeStart !== null
      || this.m_consolePlacementWait !== null
      || this.m_playbackCleanup !== null;
    if (isLive) {
      if (this.m_releasePresenterDelegation !== null) return;
      const permissions = this.m_resolvePermissions();
      if (!permissions) return;
      this.m_releasePresenterDelegation = permissions.beginPresenterDelegation();
      return;
    }
    const release = this.m_releasePresenterDelegation;
    if (release === null) return;
    this.m_releasePresenterDelegation = null;
    release();
  }

  /**
   * Starts the script runner's end-of-playback cleanup and keeps the presenter
   * delegated to Spark until it settles.
   *
   * The cleanup is what puts the scene back — every temporary change the run
   * made — and the playback mutation scope stays open for all of it. Handing the editor back before it settles let an author's first
   * edit land in the middle of that restore: kept out of the database by the
   * scope, and then overwritten or orphaned by the restore behind it.
   */
  private finishPlaybackKeepingDelegation(): void {
    const cleanup = this.m_scriptRunner.finishWorkflowPlaybackAsync?.() ?? Promise.resolve();
    const tracked: Promise<void> = cleanup
      .catch((error: unknown) => {
        console.warn("[WorkflowRunModel] End-of-run cleanup failed:", error);
      })
      .then(() => {
        if (this.m_playbackCleanup === tracked) this.endPlaybackCleanupWait();
      });
    this.m_playbackCleanup = tracked;
    if (this.m_playbackCleanupCeiling !== null) clearTimeout(this.m_playbackCleanupCeiling);
    this.m_playbackCleanupCeiling = setTimeout(() => {
      if (this.m_playbackCleanup !== tracked) return;
      console.warn(
        "[WorkflowRunModel] End-of-run cleanup is still running after "
          + `${K_PRESENTER_DELEGATION_CLEANUP_CEILING_MS} ms; handing the editor back anyway.`,
      );
      this.endPlaybackCleanupWait();
    }, K_PRESENTER_DELEGATION_CLEANUP_CEILING_MS);
    this.syncPresenterDelegation();
  }

  private endPlaybackCleanupWait(): void {
    if (this.m_playbackCleanupCeiling !== null) {
      clearTimeout(this.m_playbackCleanupCeiling);
      this.m_playbackCleanupCeiling = null;
    }
    this.m_playbackCleanup = null;
    this.syncPresenterDelegation();
  }

  /**
   * Opens console placement ahead of the run when the workflow asks for it
   * (`requires_console_placement`), and reports whether the start must WAIT.
   *
   * The workflow only makes sense with the stage sitting somewhere particular
   * in the wearer's real room, so being asked where is part of starting it.
   * The start is parked rather than refused: `tryStart` returns the workflow
   * NAME, which keeps play mode on, so the guided chrome is already up and the
   * placement bar is the only slate — `XRPanelHost` hides every other one for
   * the duration.
   *
   * Every reason to skip is a reason to start NOW rather than to wait, because
   * a wait nothing can end is a workflow that never plays: no gate injected, a
   * probe that throws, no live session, or the mode having already been
   * satisfied for this attempt.
   */
  private beginConsolePlacementIfRequired(document: WorkflowDocument): boolean {
    if (this.m_consolePlacementDone) return false;
    if (!isConsolePlacementRequired(document)) return false;

    const placement = this.m_consolePlacement;
    if (!placement) return false;

    let isAvailable = false;
    try {
      isAvailable = placement.isAvailable();
    } catch (e) {
      console.warn(
        "[WorkflowRunModel] console-placement probe failed; starting without it:",
        e,
      );
      return false;
    }
    if (!isAvailable) return false;

    // Already placing — the wearer opened it themselves and pressed Play. Wait
    // it out, but do not claim to have opened it: closing it is their call.
    const weOpenedIt = !placement.model.isPlacementMode;
    // Parked BEFORE the call, so a mode change emitted synchronously out of
    // `beginPlacement` cannot land on a model that is not yet waiting.
    this.m_consolePlacementWait = { weOpenedIt };
    if (weOpenedIt) placement.model.beginPlacement();
    this.onRunStateChanged.emit();
    return true;
  }

  /**
   * Drops a parked start and closes the placement mode if we were the ones who
   * opened it. Nulls the wait FIRST, so the `endPlacement` below re-enters the
   * subscription with nothing to resume.
   */
  private cancelConsolePlacementWait(): void {
    const wait = this.m_consolePlacementWait;
    this.m_consolePlacementWait = null;
    this.m_consolePlacementDone = false;
    if (wait?.weOpenedIt) this.m_consolePlacement?.model.endPlacement();
    if (wait !== null) this.onRunStateChanged.emit();
  }

  get isRunning(): boolean {
    return this.m_isRunning;
  }

  get isAwaitingConsolePlacement(): boolean {
    return this.m_consolePlacementWait !== null;
  }

  get isSceneHidden(): boolean {
    return this.m_isSceneHidden;
  }

  get currentStepName(): string | null {
    return this.m_currentStepName;
  }

  get visitedStepNames(): readonly string[] {
    return this.m_visitedStepNames;
  }

  get visitStack(): readonly string[] {
    return this.m_visitStack;
  }

  get resumePointStepName(): string | null {
    return this.m_resumePointStepName;
  }

  hasVisitedStep(stepName: string): boolean {
    const name = stepName.trim().toLowerCase();
    if (name.length === 0) return false;
    return this.m_visitedStepNames.some((visited) => visited.toLowerCase() === name);
  }

  getUserState(): WorkflowRunState | null {
    const document = this.m_workflowModel.loadedWorkflow;
    if (!document) return null;
    return this.m_isRunning ? this.buildWorkflowState(document) : this.m_persistedState;
  }

  /**
   * Whether pressing Play would do anything: a loaded workflow with at least
   * one step that validates clean. There is no authored "ready" flag — a valid
   * workflow is playable, and an invalid one names its errors in the panel's
   * issue queue rather than refusing silently. Broken scene/part references are
   * warnings, so a workflow whose project moved under it still plays.
   *
   * The panel's Preview button reads THIS, so the button and {@link tryStart}
   * cannot disagree.
   */
  get canStart(): boolean {
    return isWorkflowPlayable(
      this.m_workflowModel.loadedWorkflow,
      this.m_workflowModel.lastValidationReport,
    );
  }

  async tryStart(): Promise<string | null> {
    let document = this.m_workflowModel.loadedWorkflow;
    if (document === null || !this.canStart) return null;

    const workflowName = document.workflowName?.trim() ? document.workflowName : "(unnamed)";
    if (this.m_isRunning) return workflowName;

    // Placement comes FIRST when the workflow asks for it, and parks the rest
    // of this method until the wearer is done. Before the generation bump on
    // purpose: nothing has been started yet, so there is nothing to invalidate.
    if (this.beginConsolePlacementIfRequired(document)) return workflowName;

    const generation = ++this.m_startGeneration;
    await this.prepareUserStateAsync(document);
    if (generation !== this.m_startGeneration) return null;

    const currentDocument = this.m_workflowModel.loadedWorkflow;
    if (
      !currentDocument
      || currentDocument.uid !== document.uid
      || currentDocument.projectId !== document.projectId
      || !this.canStart
    ) {
      return null;
    }
    document = currentDocument;
    await this.m_scriptRunner.beginWorkflowPlaybackAsync?.();
    if (
      generation !== this.m_startGeneration
      || this.m_workflowModel.loadedWorkflow !== document
      || !this.canStart
    ) {
      await this.m_scriptRunner.finishWorkflowPlaybackAsync?.();
      return null;
    }

    this.m_isRunning = true;
    // Belt: play mode already took the delegation at the press. A caller that
    // reached this without the flag still hands the scene to Spark.
    this.syncPresenterDelegation();
    this.loadRunState(document, this.m_persistedState);
    if (!this.m_currentStepName) {
      // The first PLAYABLE step, not `steps[0]`: a workflow whose opening steps
      // are parked starts on the first live one. `canStart` has already refused
      // the all-parked case, so there is always one.
      this.m_currentStepName = getPlayableSteps(document)[0].name;
    }
    writeSceneNavigationDiagnostic("WorkflowRunModel", "run starting", {
      workflowName,
      currentStepName: this.m_currentStepName,
      activeSceneId: this.m_project.activeSceneId,
      loadedStateCurrent: this.m_persistedState?.current ?? null,
    });
    // Before `syncVisitStackToCurrentStep`, which marks this step visited —
    // afterwards every start would look like a revisit. It is only genuinely
    // one when `remember_progress` restored covered ground that already names
    // the step being resumed onto.
    const isRevisit = this.hasVisitedStep(this.m_currentStepName ?? "");
    this.syncVisitStackToCurrentStep();
    this.clearResumePointWhenReached();

    this.startWorkflowActivity(document);
    // A fresh run starts on whatever is in front of the editor, so a hide the
    // LAST run left behind (a Play pressed while its stop was still returning to
    // the first scene) is not this run's to keep. The first step re-takes it
    // below if it is media-only.
    this.m_isSceneHidden = false;
    this.m_pendingSceneReveal = null;
    // Arm the first step's Next gate BEFORE announcing the run — the panel
    // snapshots its view inside that emit. See `beginStepActivation`.
    const activation = this.beginStepActivation(document, this.m_currentStepName, isRevisit);
    this.persistWorkflowState();
    this.onRunStateChanged.emit();

    // A START announced between the state emit and the first step's script, as
    // an arrival is (`arriveAtStep`): the state is final by now, and the step's
    // first `say` has not been sent, so a line a subscriber adds to the
    // conversation lands ahead of the narration. See `onRunStarted`.
    const playableSteps = getPlayableSteps(document);
    const startStepName = this.m_currentStepName;
    this.onRunStarted.emit({
      workflowName,
      stepName: startStepName,
      position:
        Math.max(
          playableSteps.findIndex((s) => s.name.toLowerCase() === startStepName.toLowerCase()),
          0,
        ) + 1,
      stepCount: playableSteps.length,
    });

    this.runStepActivation(document, activation);
    return workflowName;
  }

  stop(): void {
    this.m_startGeneration += 1;
    // Before the early return, deliberately: while a start is parked on console
    // placement the run is NOT yet marked running, so a stop that returned here
    // would leave the wearer in a placement mode with a start still waiting to
    // fire behind it.
    this.cancelConsolePlacementWait();
    if (!this.m_isRunning) return;

    const document = this.m_workflowModel.loadedWorkflow;
    // A run that reached an AUTHORED ending is complete even when a branch left
    // earlier steps untouched, which `isWorkflowComplete` cannot see — it only
    // knows the every-step case. `dispose()` already read the activity's own
    // flag for exactly this; `stop()` now agrees with it rather than
    // contradicting it on the same run.
    this.stopWorkflowActivity(
      this.isWorkflowComplete(document) || (this.m_activeActivity?.isComplete ?? false),
    );
    this.m_isRunning = false;
    this.m_stepActivationGeneration++;
    this.clearCompletionGate();
    this.m_aiInputHold = null;
    // Where the NEXT run picks up. A detour's resume point is superseded here
    // on purpose: once the run has ended, "where to continue" is where they
    // actually left off, not where they were before the last rewind.
    this.m_resumePointStepName = this.m_currentStepName;
    // Play-mode teardown cannot see workflow scripts or an in-flight spoken
    // line; stop both here.
    this.interruptPlayback();
    // Keep the project mutation scope active until the asynchronous revert
    // chain has restored/discarded every transient playback change — and keep
    // the editor locked for as long as it takes.
    this.finishPlaybackKeepingDelegation();
    this.persistWorkflowState();
    this.onRunStateChanged.emit();

    /*
      A VIEWER leaves the project whenever playback ends — the run finishing,
      and the Exit button in the run's header alike (user direction,
      2026-08-27: "when i click exit workflow button ... it should exit project
      to project list for that kind of user").

      ⚠ This SUPERSEDES the earlier "Stop is not completion and exits nothing".
      That rule confused two different questions: whether the run is recorded
      COMPLETE (which `stopWorkflowActivity` above still decides, and which Stop
      still does not grant) and where the user LANDS. For someone who cannot
      author, landing in the editor is the bug either way, so the destination is
      uniform and only the record distinguishes the two.
      Living here rather than at the two completion sites is what makes that
      true by construction: `stop()` is the one place playback ends while the
      project is open. Project unload, sign-out and a document swap go through
      `resetRunState`, which deliberately does NOT call this — there is no
      project left to leave.
      Last, after the state is settled and `onRunStateChanged` has fired, so a
      subscriber that routes never sees a half-stopped run.

      …and an AUTHOR, who stays, is put back on the project's FIRST scene (user
      direction, AI-1357: "The project should be open on the first scene"). Here
      and not in the agent's stop tool, for the reason `requestStop` gives: the
      Exit button, the tool and an authored ending all end the run through this
      line, and a stop that left the author wherever the last step happened to
      be for one of them and on scene one for another would be three outcomes.
    */
    const leftProject = document !== null && this.requestProjectExitIfViewer(document);
    if (!leftProject) this.returnToFirstScene();
  }

  /**
   * The agent asked to end the run (`stop_workflow`), or anything else that
   * wants the Exit button's exact behaviour without going through the button.
   *
   * ⚠ IT ROUTES THROUGH THE PROJECT FLAG, NEVER STRAIGHT TO `stop()`, and that
   * is the whole of why this method exists rather than the caller doing it.
   * `isSceneStartPlayModeEnabled` is what the editor shell, the tool palette
   * and the Spark panel all watch, so calling `stop()` alone would end the run
   * while leaving the app in play mode with nothing playing — the same trap
   * `finishRunOnCompletionStep` records one screen down.
   *
   * Everything past the flip is `stop()`'s and is deliberately NOT restated
   * here: the scripts and the spoken line are interrupted, the activity is
   * closed, the resume point is written, and a VIEWER is asked to leave the
   * project while an author stays in the editor, back on its first scene. That
   * last branch is the one
   * that must not be duplicated — it is the difference between the two kinds of
   * user, and the tool inherits it by construction rather than re-deriving it.
   *
   * A start parked on console placement counts as something to stop: the flag
   * flip reaches `stop()`, which cancels that wait BEFORE its not-running early
   * return.
   */
  requestStop(reason: string): boolean {
    if (!this.m_isRunning && !this.isAwaitingConsolePlacement) return false;

    writeSceneNavigationDiagnostic("WorkflowRunModel", "stop requested", {
      reason,
      stepName: this.m_currentStepName,
      workflowName: this.m_workflowModel.loadedWorkflow?.workflowName ?? null,
      wasRunning: this.m_isRunning,
      wasAwaitingConsolePlacement: this.isAwaitingConsolePlacement,
    });

    // `stop()` runs from here, synchronously.
    this.m_project.isSceneStartPlayModeEnabled = false;
    // Belt, for the same reason the completion step carries one: the setter
    // no-ops when the flag is already false, and a run that somehow started
    // without it would otherwise be left with no way out of playback.
    if (this.m_isRunning) this.stop();
    return true;
  }

  /**
   * The agent asked to START the run (`start_workflow`) — the Play button's
   * exact behaviour without the button, and {@link requestStop}'s twin.
   *
   * ⚠ IT ROUTES THROUGH THE PROJECT FLAG, NEVER STRAIGHT TO `tryStart()`, for
   * the reason `requestStop` gives in reverse: that flag is what the editor
   * shell, the tool palette and the Spark panel all watch, so a run started
   * without it would play under an editor that does not know it is playing.
   * Flipping it is also what makes a spoken start indistinguishable from a
   * pressed one — {@link startOrDropPlayMode} runs from the flip, `tryStart`
   * owns every refusal after the one below, and a refused start drops the flag
   * again exactly as a refused Play press does.
   *
   * What this adds is the WAIT. The flag's own start is fire-and-forget,
   * because it reads the user's persisted progress before the first step plays;
   * the caller has to say what happened, so it awaits that same start and reads
   * the outcome off the model afterwards rather than predicting it.
   *
   * Play mode can already be ON with nothing running and nothing parked: a Play
   * press or an autostart landed a moment ago and its start is still settling.
   * That start is awaited, not doubled. A flag stuck on with no start behind it
   * — which nothing should leave — gets a start of its own, the belt
   * `requestStop` carries for the opposite case.
   */
  async requestStartAsync(reason: string): Promise<WorkflowStartOutcome> {
    if (this.m_isRunning) return "already_running";
    if (this.isAwaitingConsolePlacement) return "awaiting_console_placement";
    // `tryStart` would refuse too, but only after turning play mode on and off
    // again — a flicker of the playback chrome for a start that could never
    // happen. `canStart` is the one definition both read.
    if (!this.canStart) return "not_playable";

    writeSceneNavigationDiagnostic("WorkflowRunModel", "start requested", {
      reason,
      workflowName: this.m_workflowModel.loadedWorkflow?.workflowName ?? null,
      playMode: this.m_project.isSceneStartPlayModeEnabled,
      startInFlight: this.m_pendingPlayModeStart !== null,
    });

    if (!this.m_project.isSceneStartPlayModeEnabled) {
      // `startOrDropPlayMode` runs from here, synchronously.
      this.m_project.isSceneStartPlayModeEnabled = true;
    } else if (this.m_pendingPlayModeStart === null) {
      this.startOrDropPlayMode();
    }
    await this.m_pendingPlayModeStart;

    if (this.m_isRunning) return "started";
    if (this.isAwaitingConsolePlacement) return "awaiting_console_placement";
    return "not_started";
  }

  goToStep(stepName: string, cause: WorkflowMoveCause): boolean {
    const document = this.m_workflowModel.loadedWorkflow;
    if (!this.m_isRunning || !document) return false;

    // A parked step answers null here exactly as a deleted one does, so every
    // caller's existing refusal covers it with no second branch.
    const step = findPlayableStep(document, stepName);
    if (!step) return false;

    // Onward: this arrival EXTENDS the path, even onto a step already on it.
    this.arriveAtStep(document, step, { rewindTo: null, completesCurrent: false, cause });
    return true;
  }

  goBackToStep(stepName: string, cause: WorkflowMoveCause): boolean {
    const document = this.m_workflowModel.loadedWorkflow;
    if (!this.m_isRunning || !document) return false;

    const step = findPlayableStep(document, stepName);
    if (!step) return false;
    // Back means covered ground. The monotonic list answers that, not the
    // path — a step rewound off the stack was still SEEN.
    if (!this.hasVisitedStep(step.name)) return false;

    // Rewind to the LAST time they were there: everything after it is the
    // detour they are undoing, and leaving those entries on the stack is
    // exactly what would let two "back one"s bounce between two steps.
    const rewindTo = this.findLastVisitIndex(step.name);
    if (rewindTo === this.m_visitStack.length - 1) return false; // already there

    this.arriveAtStep(document, step, {
      // Covered ground that an earlier rewind already dropped off the path has
      // no route left to unwind, so the move APPENDS. The alternative — refuse
      // it — would make a step less reachable the more the learner moved
      // around, which is the opposite of what the covered-ground rule is for.
      rewindTo: rewindTo < 0 ? null : rewindTo,
      completesCurrent: false,
      cause,
    });
    return true;
  }

  goBackSteps(count: number, cause: WorkflowMoveCause): boolean {
    const document = this.m_workflowModel.loadedWorkflow;
    if (!this.m_isRunning || !document) return false;
    if (!Number.isInteger(count) || count < 1) return false;

    const targetIndex = this.m_visitStack.length - 1 - count;
    if (targetIndex < 0) return false;

    // A step renamed, deleted OR parked mid-run leaves a name the run can no
    // longer reach. Refuse rather than land the learner on a step that is not
    // there; the caller's message names the path so the agent can pick another
    // entry.
    const step = findPlayableStep(document, this.m_visitStack[targetIndex]);
    if (!step) return false;

    this.arriveAtStep(document, step, { rewindTo: targetIndex, completesCurrent: false, cause });
    return true;
  }

  replayCurrentStep(cause: WorkflowMoveCause): boolean {
    const document = this.m_workflowModel.loadedWorkflow;
    if (!this.m_isRunning || !document || !this.m_currentStepName) return false;

    const step = findPlayableStep(document, this.m_currentStepName);
    if (!step) return false;

    // Asked BEFORE the re-arrival clears the gate. A wait the learner has not
    // sat through yet is re-armed from scratch — a replay must not be a way to
    // waive it — while a wait already met, or a step with none, stays open:
    // the default revisit rule, since the current step is always covered.
    const isGatePending = this.describeNextGate(step).isBlocked;

    // The video FIRST. A same-step arrival changes nothing the panel's media
    // watchers key on (step, url, kind, trim), so without this the clip would
    // resume from wherever it was — "show me that again" would play the tail.
    // Best-effort: a step with no video, or one still loading, answers a refusal
    // here and has nothing to reset.
    this.controlStepMedia({ kind: "reset_to_arrival" });

    this.arriveAtStep(document, step, {
      // The current entry is the last one on the path, so rewinding to it drops
      // nothing: a replay is not a move, and the resume point is untouched.
      rewindTo: this.m_visitStack.length - 1,
      completesCurrent: false,
      cause,
      isRevisit: !isGatePending,
    });
    return true;
  }

  getPlaybackView(): WorkflowPlaybackView | null {
    const document = this.m_workflowModel.loadedWorkflow;
    const step =
      this.m_isRunning && document && this.m_currentStepName
        ? findPlayableStep(document, this.m_currentStepName)
        : null;
    if (!document || !step) return null;

    // Every count and position here is over the PLAYABLE steps: a parked step
    // is not a position the learner is measured against, so "3 of 7" must not
    // include steps they can never be shown.
    const playableSteps = getPlayableSteps(document);
    const overallIndex = playableSteps.indexOf(step);
    const group = step.groupName.length > 0 ? document.findGroup(step.groupName) : null;
    // "n of m" is group-scoped when the step has a group, workflow-wide otherwise.
    const scopeSteps = group ? getPlayableGroupSteps(group) : playableSteps;
    const scopeIndex = scopeSteps.indexOf(step);
    // Runtime tokens (`<user name>`) resolve HERE, at the display boundary, and
    // nowhere upstream of it: the document keeps the literal so the author can
    // edit it and every learner gets their own name from the same file. Names
    // are deliberately not passed through — see `workflowTextTokens.ts`: a step
    // name is what `go_to`, the visit stack and the agent's
    // `go_to_workflow_step` all address, so a substituted one would show the
    // learner a step they could not then ask to return to.
    const tokens = this.textTokenContext();
    const instructions = resolveWorkflowText(getStepInstructions(step), tokens);
    const gate = this.describeNextGate(step);
    const media = resolvePlaybackMedia(step, tokens);

    return {
      workflowName: document.workflowName?.trim() ? document.workflowName : "(unnamed)",
      stepName: step.name,
      groupName: group ? group.name : null,
      positionIndex: Math.max(scopeIndex, 0) + 1,
      positionCount: scopeSteps.length,
      overallIndex: Math.max(overallIndex, 0) + 1,
      overallCount: playableSteps.length,
      instructions,
      // Emptiness IS the hidden state — there is no separate display switch.
      showInstructions: instructions.length > 0,
      // Only NEXT follows the instructions' rule, where the label IS the
      // switch and null means no button. Back's label is optional decoration
      // on a button `hasPrevious` decides the existence of, so null here means
      // "draw the arrow alone".
      backButtonLabel: resolveNullableText(getStepBackButtonLabel(step), tokens),
      nextButtonLabel: resolveNullableText(getStepNextButtonLabel(step), tokens),
      // Back walks the learner's PATH, so what SHOWS it is having somewhere to
      // walk back TO — not the step's position in the document, and not
      // anything the author wrote. On the first arrival of a run there is
      // nothing behind them even if the document has ten steps before this one
      // (a trigger may have started them in the middle).
      hasPrevious: this.m_visitStack.length > 1,
      hasNext: overallIndex >= 0 && overallIndex + 1 < playableSteps.length,
      // What Next DOES here, which `hasNext` cannot say: an authored ending can
      // sit anywhere in a branching workflow, so a step may have a successor in
      // the document and still be the one the learner finishes on.
      isCompletionStep: isCompletionStep(step),
      isNextBlocked: gate.isBlocked,
      nextBlockedReason: gate.reason,
      userInputAllowed: resolveUserInputAllowed(document, step),
      isMobileKeyboardDisabled: isMobileKeyboardDisabled(document),
      // The same latch, published TWICE because its two consumers ask
      // different questions of it and only one of them is switchable.
      //
      // The raw fact: this step's on-start script has the floor. Read by the
      // executor to keep the Guide from replying over the author's lines, which
      // is true whatever the composer is doing.
      isOnStartScriptHoldingFloor: this.m_aiInputHold !== null,
      // The composer DECISION, which is the raw fact gated by the flag and is
      // therefore false throughout while it is off. Kept APART from
      // `userInputAllowed` on `isNextBlocked`'s precedent: that list is what the
      // AUTHOR opened, this is a temporary run-time hold on one of its members.
      // Folding the hold into the list would make "the learner may ask questions
      // on this step" unanswerable.
      isAiInputHeld: K_HOLD_AI_INPUT_DURING_ON_START_SCRIPT && this.m_aiInputHold !== null,
      attachment:
        step.attachment === null
          ? null
          : { part: step.attachment.part, point: step.attachment.point },
      media,
      // One definition, shared with the scene hide in `beginStepActivation`, so
      // the panel can never expand over a scene the run left on screen.
      isMediaOnly: isMediaOnlyStep(step),
      triggers: step.triggers
        .map((trigger, index) => ({ trigger, index }))
        .filter(
          ({ trigger }) =>
            !trigger.isInvalid &&
            trigger.invalidType.length === 0 &&
            trigger.label.trim().length > 0 &&
            // A button whose target is PARKED has nowhere live to go, and
            // `activateTrigger` would refuse the press — so it is not rendered
            // at all rather than shown as a control that visibly does nothing.
            // `validate()` warns the author about exactly this.
            findPlayableNavigationTarget(document, trigger.goTo) !== null,
        )
        .map(({ trigger, index }) => ({
          index,
          // The LABEL is display prose; `target` stays raw because it names a
          // step, and `index` is what a press dispatches on.
          label: resolveWorkflowText(trigger.label, tokens),
          target: trigger.goTo,
        })),
    };
  }

  advanceToNextStep(cause: WorkflowMoveCause): boolean {
    const document = this.m_workflowModel.loadedWorkflow;
    const step =
      this.m_isRunning && document && this.m_currentStepName
        ? findPlayableStep(document, this.m_currentStepName)
        : null;
    if (!document || !step) return false;

    // The step's completion condition holds NEXT and nothing else: a trigger
    // button still branches and Back still goes back, because both are the
    // learner leaving the step rather than finishing it. This is also what the
    // agent's `direction=next` hits — it goes through this same call, so the
    // panel's disabled button and the agent's move cannot disagree about
    // whether the step is passable yet.
    if (this.describeNextGate(step).isBlocked) return false;

    // An authored ending takes precedence over document order: the author has
    // said this step is where the workflow finishes, so Next completes it and
    // leaves play mode rather than walking on into steps that are only there
    // for other branches to reach.
    if (isCompletionStep(step)) {
      this.finishRunOnCompletionStep(document, step);
      return true;
    }

    // Next completes the step and moves on, full stop. There is no
    // on-completion script to play first: a step's only prepared script runs
    // when it becomes current, so Next is pure navigation and never waits on
    // anything the learner cannot see. (`arriveAtStep` is what interrupts the
    // outgoing step's script and speech, on the branch that has a next step;
    // the last-step branch does it below.)
    this.advancePastCurrentStep(cause);
    return true;
  }

  /**
   * Back is a move along the learner's own path, not along the document. Which
   * matters exactly when the two disagree: after a trigger branched them past a
   * run of steps, document-order Back would drop them into ground they have
   * never seen — the same skip the forward gate exists to prevent.
   */
  goToPreviousStep(cause: WorkflowMoveCause): boolean {
    return this.goBackSteps(1, cause);
  }

  activateTrigger(triggerIndex: number, movedBy: WorkflowMover): boolean {
    const document = this.m_workflowModel.loadedWorkflow;
    const step =
      this.m_isRunning && document && this.m_currentStepName
        ? findPlayableStep(document, this.m_currentStepName)
        : null;
    if (!document || !step) return false;

    // Addressed by SOURCE index, not label: duplicate labels are legal (a
    // warning), and a label lookup would resolve every duplicate to the
    // first entry's target.
    const trigger = step.triggers[triggerIndex];
    if (
      !trigger ||
      trigger.isInvalid ||
      trigger.invalidType.length > 0 ||
      trigger.label.trim().length === 0
    ) {
      return false;
    }

    // Playback-scoped: a group whose opening steps are parked lands on its
    // first live one, and a button pointing straight at a parked step refuses
    // (the panel does not render one — see `getPlaybackView`).
    const target = findPlayableNavigationTarget(document, trigger.goTo);
    if (!target) return false;

    // A trigger button navigates WITHOUT completing the current step, and it
    // is authored BRANCHING — an onward move even when it points backwards, so
    // it extends the path rather than rewinding it. That is what makes a step
    // legitimately appear on the path more than once.
    //
    // The label goes out RESOLVED: it names the button as the learner saw it,
    // and a label is display prose rather than a name anything addresses by.
    // Read HERE rather than taken from the caller so it always names the button
    // `triggerIndex` actually pressed.
    return this.goToStep(target.name, {
      by: movedBy,
      via: "trigger",
      label: resolveWorkflowText(trigger.label, this.textTokenContext()),
    });
  }

  /**
   * The runtime facts a workflow's authored prose may refer to — today just the
   * learner's first name, for `<user name>`.
   *
   * Read from `m_activity.auth` because that is the auth model this class
   * already has (it keys the per-user progress row off the same
   * `currentUser`). Absent activity deps mean a headless run with no signed-in
   * user, and the token then resolves to nothing rather than to an invented
   * stand-in — see `workflowTextTokens.ts`.
   *
   * Derived per read rather than cached: `currentUser` changes on sign-in and
   * sign-out, and a cached name would outlive the session it belonged to.
   */
  private textTokenContext(): WorkflowTextTokenContext {
    return { userFirstName: resolveUserFirstName(this.m_activity?.auth.currentUser ?? null) };
  }

  /**
   * The run-state bullets for the prompt's `# Runtime Conversation Instructions`
   * section: WHERE the run is, never what the workflow says.
   *
   * The document itself is deliberately absent. It used to ride every single
   * turn as a `# Current Workflow` block holding the whole serialized file;
   * it is now pulled ON DEMAND with `get_workflow` by the two roles that hold
   * that tool, the same progressive disclosure the published schema already
   * uses. These bullets therefore name no tool: they reach every role,
   * including ones with no workflow tool at all.
   */
  buildRuntimePromptInstructions(): string | null {
    const document = this.m_workflowModel.loadedWorkflow;
    if (!document) return null;

    const name = document.workflowName?.trim() ? document.workflowName : "(unnamed)";
    const bullets: string[] = [];
    if (this.m_isRunning && this.m_currentStepName) {
      // Playable-scoped throughout: telling the guide "3 of 7" while the panel
      // shows "3 of 5" would have it narrate a position the learner is not in.
      const playableSteps = getPlayableSteps(document);
      const stepIndex = Math.max(
        0,
        playableSteps.findIndex(
          (t) => t.name.toLowerCase() === this.m_currentStepName!.toLowerCase(),
        ),
      );
      const groupName = playableSteps[stepIndex]?.groupName ?? "";
      const position =
        groupName.length > 0
          ? `${stepIndex + 1} of ${playableSteps.length}, in group "${groupName}"`
          : `${stepIndex + 1} of ${playableSteps.length}`;
      bullets.push(
        `- The workflow "${name}" is running. Current step: "${this.m_currentStepName}" (${position}).`,
      );

      // The covered ground IS the agent's navigation limit, so the run has to
      // state it. It cannot be inferred from the position: a trigger button can
      // branch the learner over steps they never saw, and completion is a
      // different question again (only Next completes).
      const covered = playableSteps
        .filter((step) => this.hasVisitedStep(step.name))
        .map((step) => `"${step.name}"`);
      bullets.push(
        `- Steps the learner has reached so far, in order: ${covered.join(", ")}. Those are the only ones you may take them back to; everything else is ground they have not seen, and they reach it by continuing.`,
      );

      // The PATH, not the covered ground: "go back three steps" counts moves,
      // and the two lists differ as soon as a trigger repeats a step or a
      // rewind drops one. Numbering it is what lets the model answer "how far
      // back is X" without re-deriving the route.
      const depth = this.m_visitStack.length - 1;
      const path = this.m_visitStack.map((name, i) => `${i + 1}. "${name}"`).join(", ");
      bullets.push(
        `- The path they actually took, in order, ending where they are now: ${path}. ` +
          (depth > 0
            ? `You can rewind it with go_to_workflow_step back=<1-${depth}> when they ask to go back a number of steps; going back drops the steps they returned from, so counting always starts from where they are now.`
            : "They are at the start of it, so there is nothing to go back to yet."),
      );

      if (this.m_resumePointStepName) {
        bullets.push(
          `- They were on "${this.m_resumePointStepName}" before they were taken back, and have not returned there. That is where they continue from - offer it rather than restarting, and get them there with direction=next or by naming that step.`,
        );
      }

      const currentStep = playableSteps[stepIndex] ?? null;
      if (currentStep && isCompletionStep(currentStep)) {
        bullets.push(
          "- This step is the workflow's ENDING: pressing Next finishes the run and leaves playback, "
            + "so there is nothing after it. Wrap up here rather than describing what comes next, and "
            + "do not offer to continue.",
        );
      }

      const view = this.getPlaybackView();

      // A gated step has to be a bullet: the Guide narrates every turn, and
      // without this it would tell the learner to press a button that is
      // greyed out and only discover the gate by being refused. It names no
      // tool, like every bullet here — the roles that can move the run have
      // their own refusal, and the ones that cannot still have to describe
      // what the learner is looking at.
      if (view?.isNextBlocked) {
        bullets.push(
          `- Next is not available on this step yet: ${view.nextBlockedReason}. The learner's ` +
            "button is disabled until then and the run cannot be advanced, so tell them what they " +
            "are waiting for rather than asking them to press it.",
        );
      }

      // ...and the wait ENDING has to be a bullet too, which is the half that
      // was missing. The blocked bullet simply disappearing is not a statement
      // the model can act on: it told the learner last turn that the step was
      // still playing, that sentence is in the conversation it is reading, and
      // an absence contradicts nothing — so it went on saying the step was
      // playing after the button had gone live. Same shape as the attribution
      // and bounding-box replays: an agent answers from the transcript, so a
      // change of state has to be SAID.
      //
      // No `else` needed: `describeSettledGate` answers only for a gate that is
      // SATISFIED and `isNextBlocked` only for one that is not, so the two are
      // mutually exclusive by construction. It then rides every turn for the
      // rest of the step, which is correct — the fact stays true until the next
      // arrival, and it only exists on a step that was actually gated.
      const settledGate = currentStep ? this.describeSettledGate(currentStep) : null;
      if (settledGate) {
        bullets.push(
          `- The wait on this step is over: ${settledGate}, so Next is available now and the ` +
            "learner's button is live. If you told them earlier that they were waiting for it, say " +
            "it has finished rather than repeating the wait.",
        );
      }

      // Same argument as the gate above, and the same failure it prevents: the
      // Guide narrates every turn — a script `say` IS a turn — so without this
      // it would invite the learner to type into a box that is not on screen.
      //
      // Reads the gated `isAiInputHeld` and not the raw latch, deliberately:
      // this bullet is a claim about what the LEARNER can see, so it must be
      // silent whenever the composer is in fact on screen. With
      // `K_HOLD_AI_INPUT_DURING_ON_START_SCRIPT` off that is always, so the
      // bullet never fires — which is the honest outcome, not a gap. Do not
      // "fix" it onto `isOnStartScriptHoldingFloor`: that would tell the Guide
      // a box the learner is looking at is collapsed.
      if (view?.isAiInputHeld) {
        bullets.push(
          "- The learner's chat box is collapsed while this step's script plays, and comes back when "
            + "it finishes. Do not ask them to type or answer anything yet.",
        );
      }

      // Says the labels VERBATIM because they are what the learner reads aloud:
      // this bullet is the list the model matches a spoken answer against, and
      // `go_to_workflow_step trigger=` takes the label rather than their words.
      // Naming the tool the way the path bullet does — a role without it still
      // reads the sentence as what the learner is looking at, which it also is.
      //
      // ⚠ It also carries the MOVE-ON rule for this step, and has to (2026-09-23,
      // reported twice: "Now continue" and, after one push-back, "Now keep
      // going." each pressed "Area is clear" on a step with no Next). The role
      // prompt teaches move-on replies as "advance the run" and qualifies it for
      // button steps; a qualifier on shared words is the one the model drops
      // (`Campfire_Web_AI.md` → disjoint phrases). This bullet exists ONLY on a
      // step with buttons, so here the rule needs no qualifier at all — it is
      // simply true — and it sits beside the labels it is about, on every turn.
      // Whether the step ALSO has a Next decides which way a plain "continue"
      // goes, so that is said too rather than left for the model to infer.
      if (view && view.triggers.length > 0) {
        const buttons = view.triggers
          .map((trigger) => `"${trigger.label}" -> ${trigger.target}`)
          .join(", ");
        const moveOn =
          view.nextButtonLabel === null
            ? "This step has NO Next button: one of these buttons is the only way on, and which one " +
              "is the learner's answer to give, never yours. A reply that only asks to move on - " +
              "continue, next, keep going, go ahead, or asking you to pick for them - says nothing " +
              "about the step's question, so it presses nothing, however many times they say it: " +
              "call no tool, and ask the step's question again in one short line, naming the buttons. "
            : `This step also has a Next button ("${view.nextButtonLabel}"): a reply that only asks ` +
              "to move on means that one (direction=next), never one of these triggers. ";
        bullets.push(
          `- The learner's Workflow panel shows trigger buttons for this step: ${buttons}. ${moveOn}` +
            "Pressing a trigger jumps there without completing the current step. They can ANSWER out " +
            "loud instead of clicking - when their words say which button is true for them, press that " +
            "one for them with go_to_workflow_step trigger=<the label above, exactly as written>. Do " +
            "not press one they have not chosen, and do not use direction=next to stand in for one: it " +
            "completes the step and follows written order rather than the button's branch.",
        );
      }

      // The Next button's LABEL on a step with no triggers (AI-1418). The
      // trigger bullet above names it where there are buttons; everywhere else
      // the Guide had only the role's generic move-on words, and on a step
      // whose Next reads "Start" it told the learner to "say 'next'" — a word
      // on no button (2026-09-29 trace). How to ASK about moving on is the
      // step-move refusal's job (`Workflow_Executor.refuseUnaskedMoveAsync`),
      // and a learner saying the label is the step-move check's
      // (`StepMoveVocabulary.moveLabels`).
      //
      // ⚠ THE FACT AND NOTHING ELSE — measured, not a style choice. The first
      // version added "The learner saying that, or asking to move on, is them
      // pressing it. If you mention the button, call it by that label." On
      // Welcome, "Uh... so..." (a fragment `guide.md` makes silent) then got
      // `guide.md`'s fixed "Please see the instructions…" line in 2 of 6 runs,
      // against 0 of 6 with no bullet and 0 of 6 with this sentence alone
      // (2026-09-29). An instruction about speaking is an invitation to speak.
      //
      // It also says the trigger buttons are GONE, which is the other half of
      // the trigger bullet above — the same pairing as the Next gate and
      // `describeSettledGate`. A bullet that disappears contradicts nothing the
      // conversation says (2026-10-01 trace): the Guide pressed "Drones are
      // active" on a step with no Next, landed on a warning step with a Next and
      // no triggers, and answered "Looks pretty clear." with "I can't tell which
      // button that matches" — then "If the safety question used buttons, I
      // can't move on" — until the learner pressed Next themselves. Its last
      // word on buttons was its own press, and the label alone did not say the
      // buttons had gone.
      //
      // The label is the AUTHOR's and can be any wording ("Start", "Done",
      // "Area is clear"), so the bullet calls the button Next — its job, the
      // word the role and `direction=next` use — and gives the label verbatim
      // beside it. Never assume it reads "Next". A label that sounds like an
      // answer is the case that needs this most: without "no trigger buttons"
      // it reads like one more button to choose.
      //
      // ⚠ "No trigger buttons" ALONE did not do it (measured 2026-10-01, 15
      // turns on the real model): the Guide read it as "no buttons it was told
      // about" and answered "I can't safely pick a button label I don't see".
      // So the bullet names the WHOLE set the learner is looking at — Back
      // (only when it is drawn) and Next — which leaves no unseen button to
      // imagine.
      if (view && view.nextButtonLabel !== null && view.triggers.length === 0) {
        bullets.push(
          view.hasPrevious
            ? `- This step has no trigger buttons: the only buttons on the learner's panel are Back and ` +
                `Next, and its Next reads "${view.nextButtonLabel}".`
            : `- This step has no trigger buttons: the only button on the learner's panel is Next, which ` +
                `reads "${view.nextButtonLabel}".`,
        );
      }

      // A step that says nothing out loud, where "what now?" has one right
      // answer: the step's own text, and a live Next. A bullet rather than a
      // role line because the condition is state the run already knows, and a
      // role line qualified by it is the one the model drops the qualifier from
      // (`Campfire_Web_AI.md` → disjoint phrases). Every term is a place the
      // reply would be WRONG, not a nicety (2026-09-29):
      //   - a speaking on-start script — the step narrates itself;
      //   - a video — it may carry a voice-over, and the learner is watching it;
      //   - no Next, the ending, or the last step — "ready to move on" offers a
      //     move that does not exist, contradicting the trigger bullet above,
      //     the ENDING bullet's "do not offer to continue", and the role's
      //     "wrap up" on a last step;
      //   - a held Next — the gate bullet already says what they wait for.
      // Its wording carries no description of the step on purpose: a sentence
      // saying why the step is silent is a sentence the model hands the learner.
      // It names no bare "what?", which stays the role's "say that again".
      if (
        view &&
        currentStep &&
        !stepOnStartScriptSpeaks(currentStep) &&
        view.media?.kind !== "video" &&
        view.nextButtonLabel !== null &&
        view.hasNext &&
        !view.isCompletionStep &&
        !view.isNextBlocked
      ) {
        bullets.push(
          '- If the learner asks what to do on this step ("what now?", "what do I do here?"), your ' +
            `whole reply is: "${K_SILENT_STEP_REPLY}" Only as that reply - never added to an answer, ` +
            "and never for a sound or a half-sentence. Never explain why nothing was said on this step.",
        );
      }
    } else {
      // Who may START a run, said without naming a tool — these bullets reach
      // every role, including ones with nothing to start it with. The user
      // presses Play, or asks an author role, which holds `start_workflow`
      // (AI-1357). What the bullet must still rule out is the agent starting one
      // on its own initiative: a run takes the whole session over.
      const isPlayable = this.canStart;
      bullets.push(
        `- This project has a guided training workflow "${name}" (${getPlayableSteps(document).length} steps) and it is not playing. ` +
          (isPlayable
            ? "It starts when the user presses Play or asks for it to be started or previewed - never start it on your own initiative."
            : "It cannot play yet - it still has validation errors, and the workflow-builder role is what fixes them, so switch_role there or tell the user to open the Workflow editor, which switches to that role."),
      );
      // Only worth saying when the next run would actually resume: with
      // `remember_progress` absent (the default) it starts from the top.
      const persistedResume = findPlayableStep(
        document,
        this.m_persistedState?.resumePoint ?? this.m_persistedState?.current ?? "",
      )?.name;
      if (isPlayable && isProgressRemembered(document) && persistedResume) {
        bullets.push(
          `- This workflow remembers progress: the last run stopped on "${persistedResume}", and the next one picks up there.`,
        );
      }
    }

    // The learner's name, because the Guide reads the workflow through
    // `get_workflow` — which returns the FILE, tokens and all. Every surface
    // the LEARNER sees resolves `<user name>` on the way out, but a step the
    // Guide paraphrases from the document has not been through any of them, so
    // without this it would read the brackets out loud.
    //
    // Stated on every run rather than only when the document uses the token:
    // deciding that would mean a second list of "which fields may carry one",
    // kept in step with `getPlaybackView` by hand, to save one short line.
    const firstName = this.textTokenContext().userFirstName;
    if (firstName.length > 0) {
      bullets.push(
        `- The learner's first name is "${firstName}". Wherever the workflow's own text contains ` +
          "`<user name>`, that is what it stands for - say the name and never the brackets. Write " +
          "the token, not the name, if you ever edit that text: the file is shared, and a name " +
          "written into it would greet every other learner by this one's.",
      );
    }

    if (this.m_workflowModel.lastValidationReport.hasBrokenReferences) {
      bullets.push(
        "- The workflow has unresolved references: its scene or part names no longer match the project. It still plays, but offer to re-resolve them in the workflow-builder role.",
      );
    }

    if (this.m_workflowModel.lastValidationReport.hasCommandIssues) {
      bullets.push(
        "- The workflow has script steps that name a tool which does not exist, or pass it arguments it does not take. Those steps fail when they play. Offer to fix them in the workflow-builder role.",
      );
    }

    return bullets.length === 0 ? null : bullets.join("\n");
  }

  dispose(): void {
    this.cancelPendingAutostart();
    this.cancelConsolePlacementWait();
    this.clearCompletionGate();
    this.m_aiInputHold = null;
    this.stopWorkflowActivity(this.m_activeActivity?.isComplete ?? false);
    void this.m_scriptRunner.finishWorkflowPlaybackAsync?.();
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    // Nothing is left to hand the editor back when the cleanup settles.
    if (this.m_playbackCleanupCeiling !== null) clearTimeout(this.m_playbackCleanupCeiling);
    this.m_playbackCleanupCeiling = null;
    this.m_playbackCleanup = null;
    this.m_releasePresenterDelegation?.();
    this.m_releasePresenterDelegation = null;
  }

  // ---- Internals -------------------------------------------------

  private startWorkflowActivity(document: WorkflowDocument): void {
    const activity = this.m_activity;
    const user = activity?.auth.currentUser;
    const workflowId = document.uid.trim();
    if (!activity || !user?.customerId || !user.userId || !workflowId) return;
    const customerId = user.customerId;
    const userId = user.userId;

    // Defensive close: a run should already have stopped its previous
    // activity, but never let a stale pair survive into another workflow.
    this.stopWorkflowActivity(false);
    this.m_activeActivity = {
      customerId,
      userId,
      workflowId,
      stepName: null,
      stepId: null,
      isComplete: false,
    };
    this.recordActivity(() =>
      activity.service.recordWorkflowStart(customerId, workflowId, userId));

    const step = this.m_currentStepName
      ? findPlayableStep(document, this.m_currentStepName)
      : null;
    if (step) this.startStepActivity(step);
  }

  private stopWorkflowActivity(isComplete: boolean): void {
    const active = this.m_activeActivity;
    const service = this.m_activity?.service;
    if (!active || !service) {
      this.m_activeActivity = null;
      return;
    }

    this.stopStepActivity(false);
    this.recordActivity(() =>
      service.recordWorkflowStop(
        active.customerId,
        active.workflowId,
        active.userId,
        isComplete,
      ));
    this.m_activeActivity = null;
  }

  private transitionStepActivity(step: WorkflowStep, isComplete: boolean): void {
    const active = this.m_activeActivity;
    if (
      active?.stepName
      && active.stepName.toLowerCase() === step.name.toLowerCase()
    ) {
      return;
    }
    this.stopStepActivity(isComplete);
    this.startStepActivity(step);
  }

  private startStepActivity(step: WorkflowStep): void {
    const active = this.m_activeActivity;
    const service = this.m_activity?.service;
    if (!active || !service) return;

    active.stepName = step.name;
    active.stepId = step.stepId.trim() || null;
    const stepId = active.stepId;
    if (!stepId) return;
    this.recordActivity(() =>
      service.recordStepStart(
        active.customerId,
        active.workflowId,
        stepId,
        active.userId,
      ));
  }

  private stopStepActivity(isComplete: boolean): void {
    const active = this.m_activeActivity;
    const service = this.m_activity?.service;
    if (!active || !service) return;

    const stepId = active.stepId;
    active.stepName = null;
    active.stepId = null;
    if (!stepId) return;
    this.recordActivity(() =>
      service.recordStepStop(
        active.customerId,
        active.workflowId,
        stepId,
        active.userId,
        isComplete,
      ));
  }

  private isWorkflowComplete(document: WorkflowDocument | null): boolean {
    const steps = getPlayableSteps(document);
    if (steps.length === 0) return false;
    return steps.every((step) =>
      this.m_completedStepNames.some(
        (completed) => completed.toLowerCase() === step.name.toLowerCase(),
      ));
  }

  private recordActivity(record: () => void): void {
    try {
      record();
    } catch (error) {
      // Activity persistence is observational; it must never interrupt a run.
      console.warn("[WorkflowRunModel] Failed to record workflow activity:", error);
    }
  }

  /**
   * Next on a step the author marked as the workflow's ending: complete it and
   * LEAVE play mode, rather than advancing into whatever follows it in the
   * document.
   *
   * Ordering is load-bearing. The completion is recorded, the step activity is
   * closed as complete, and the run is stamped complete BEFORE play mode is
   * flipped — because flipping it re-enters this model through
   * `onSceneStartPlayModeChanged` and `stop()` reads all three on its way out.
   * Doing any of it afterwards would report the run as abandoned on the very
   * press that finished it.
   *
   * Exiting through the project flag rather than by calling `stop()` directly
   * is what makes this press indistinguishable from the panel's Stop button:
   * the flag is what the editor shell, the tool palette and the Spark panel all
   * watch, so calling `stop()` alone would end the run while leaving the app in
   * play mode with nothing playing.
   */
  private finishRunOnCompletionStep(document: WorkflowDocument, step: WorkflowStep): void {
    this.interruptPlayback();
    this.stopStepActivity(true);
    if (!this.m_completedStepNames.some((n) => n.toLowerCase() === step.name.toLowerCase())) {
      this.m_completedStepNames.push(step.name);
    }
    if (this.m_activeActivity) this.m_activeActivity.isComplete = true;

    writeSceneNavigationDiagnostic("WorkflowRunModel", "completion step ended the run", {
      stepName: step.name,
      workflowName: document.workflowName,
      activeSceneId: this.m_project.activeSceneId,
    });

    // `stop()` runs from here, synchronously, and does the rest: persist, emit,
    // and record where the next run picks up.
    this.m_project.isSceneStartPlayModeEnabled = false;
    // Belt, because the failure mode is a wedged run rather than a wrong
    // pixel: the setter no-ops when the flag is already false, and a run that
    // somehow started without it would be left with no way out of playback.
    if (this.m_isRunning) this.stop();
    // No exit request here: `stop()` above owns it for every way playback ends.
  }

  /**
   * Playback is over: for a VIEWER, leave the project too. True when the exit
   * was requested — `stop()` uses it to tell a learner who is leaving from an
   * author who is staying.
   *
   * One caller — the tail of `stop()`, which is the single place playback ends
   * while the project is open. An author stays: previewing your own workflow,
   * or stopping it, must leave you in the editor ({@link returnToFirstScene}
   * decides where in it).
   *
   * Emitting rather than routing is the layering rule — see
   * {@link IWorkflowRunModel.onProjectExitRequested}.
   */
  private requestProjectExitIfViewer(document: WorkflowDocument): boolean {
    if (!this.isViewerLearner()) return false;

    /*
      ⚠ THE RUNTIME PROJECT, NOT `document.projectId`, and the order matters.

      This asks "which project am I in?", and the document's `project.id` is
      AUTHORED data that only the SAVE path stamps — `loadInternalAsync` sets
      its own `m_loadedProjectId` and leaves the parsed document's field exactly
      as the stored YAML wrote it. So a file whose `project.id` is stale or was
      copied from elsewhere names a project the user is not in, and the
      subscriber's "is this the project I have open?" guard then correctly
      refuses to navigate — the exit silently does nothing, which is what
      "i click stop playback ... i land on project view" was.

      The active project is the only source that cannot be stale. No fallback to
      the document: if there is no active project there is nothing to leave.
    */
    const projectId = this.m_project.activeProject?.id ?? "";
    if (!projectId) return false;

    writeSceneNavigationDiagnostic("WorkflowRunModel", "viewer finished the workflow; exiting", {
      projectId,
      workflowName: document.workflowName,
    });
    this.onProjectExitRequested.emit({
      projectId,
      workflowName: document.workflowName?.trim() ? document.workflowName : "",
    });
    return true;
  }

  /**
   * The run is over and the AUTHOR stayed: put the editor back on the
   * project's FIRST scene (user direction, AI-1357 — "The project should be
   * open on the first scene").
   *
   * A run moves the editor as it plays — every step that names a scene
   * activates it — so without this a stop left the author on whichever scene
   * the last step happened to use. Fire-and-forget like a step's own
   * activation: `stop()` is synchronous and nothing after it waits on the
   * scene, and a step activation still loading is superseded by this one
   * (`stop()` has already bumped the activation generation it checks).
   *
   * A no-op when the project has no scenes or is already on the first one, so
   * stopping a preview of a one-scene project reloads nothing. Never reached
   * for a viewer, whose project is closing: activating a scene there would race
   * the unload.
   */
  private returnToFirstScene(): void {
    // A run that stopped on an empty viewport stays empty until the first
    // scene is on screen, so the scene it was hiding does not flash up on the
    // way back (user direction, 2026-10-01). Captured after `stop()`'s bump: a
    // run started before the switch lands claims a newer generation, and this
    // reveal then belongs to nobody.
    const generation = this.m_stepActivationGeneration;
    const firstScene = this.m_project.scenes[0] ?? null;
    if (!firstScene || this.m_project.activeSceneId === firstScene.id) {
      this.showSceneOnceBuilt(generation);
      return;
    }

    writeSceneNavigationDiagnostic("WorkflowRunModel", "run ended; returning to the first scene", {
      fromSceneId: this.m_project.activeSceneId,
      toSceneId: firstScene.id,
    });
    void this.m_project
      .setActiveScene(firstScene.id, "workflow_stopped:first_scene")
      .catch((error) => {
        console.warn("[WorkflowRunModel] Could not return to the first scene after the run:", error);
      })
      .finally(() => this.showSceneOnceBuilt(generation));
  }

  private advancePastCurrentStep(cause: WorkflowMoveCause): void {
    const document = this.m_workflowModel.loadedWorkflow;
    if (!document || !this.m_currentStepName) return;

    const current = this.m_currentStepName;
    // Over the playable steps, so Next steps OVER a parked one rather than
    // landing on it — that is the whole of what `disabled` does to the
    // sequence.
    const steps = getPlayableSteps(document);
    const index = steps.findIndex((t) => t.name.toLowerCase() === current.toLowerCase());
    const next = index >= 0 && index + 1 < steps.length ? steps[index + 1] : null;
    if (next) {
      this.arriveAtStep(document, next, {
        rewindTo: null,
        completesCurrent: true,
        cause,
      });
      return;
    }

    // Last step finished — the run stays on it; the guide wraps up. Nothing
    // moves, so the path is untouched, but the outgoing narration still is:
    // the learner asked to be done with this step.
    this.interruptPlayback();
    this.stopStepActivity(true);
    if (!this.m_completedStepNames.some((n) => n.toLowerCase() === current.toLowerCase())) {
      this.m_completedStepNames.push(current);
    }
    if (this.m_activeActivity) this.m_activeActivity.isComplete = true;
    this.persistWorkflowState();
    this.onRunStateChanged.emit();

    // A workflow with no authored `is_completion_step` still ENDS — here, when
    // Next is pressed on its last step. For an author that stays exactly as
    // above (the run holds so the guide can wrap up, and they can go Back), but
    // for a viewer it is a dead end: the workflow is stamped complete, they
    // cannot edit, and Next now does nothing at all. So the viewer leaves play
    // mode and then the project, which is what the authored ending does one
    // branch up — the two endings must not mean different things to the same
    // learner just because one of them was declared.
    if (!this.isViewerLearner()) return;
    this.m_project.isSceneStartPlayModeEnabled = false;
    // `stop()` runs from that flag and requests the exit; the belt covers a run
    // that somehow started without the flag.
    if (this.m_isRunning) this.stop();
  }

  /**
   * THE arrival seam. Every move the run makes — start aside — lands here, so
   * no navigation path can reach a step without the path, the covered ground,
   * the resume point, the outgoing narration and the persisted row all agreeing
   * about what just happened.
   *
   * `rewindTo` is the whole distinction the visit stack exists for: `null` means
   * this arrival EXTENDS the path (Next, a trigger, a jump onto covered ground
   * the path no longer holds), an index means it REWINDS to that entry and
   * drops everything after it.
   */
  private arriveAtStep(
    document: WorkflowDocument,
    step: WorkflowStep,
    options: {
      readonly rewindTo: number | null;
      readonly completesCurrent: boolean;
      readonly cause: WorkflowMoveCause;
      /**
       * Overrides the covered-ground answer for the Next gate. Only
       * {@link replayCurrentStep} passes it: the current step is always
       * covered, yet a replay must re-arm a wait the learner has not sat
       * through yet rather than waive it.
       */
      readonly isRevisit?: boolean;
    },
  ): void {
    const fromStepName = this.m_currentStepName;
    writeSceneNavigationDiagnostic("WorkflowRunModel", "step arrival accepted", {
      fromStepName,
      toStepName: step.name,
      reason: describeMoveCause(options.cause),
      movedBy: options.cause.by,
      rewindTo: options.rewindTo,
      completesCurrent: options.completesCurrent,
      activeSceneId: this.m_project.activeSceneId,
    });

    // Asked BEFORE `pushVisit` below, which would make every arrival a
    // revisit. This is what tells the Next gate that the learner has already
    // covered this step, so its completion condition is not charged again.
    const isRevisit = options.isRevisit ?? this.hasVisitedStep(step.name);

    // Whatever is still narrating belongs to the step being LEFT: a script
    // mid-flight (which reverts what it applied as it unwinds) and, when the
    // learner moved while Spark was mid-sentence, the spoken line itself.
    // Told which scene the move LEAVES, so Spark's changes there come off
    // once it has been left rather than on screen during the load (AI-1429).
    this.interruptPlayback(this.sceneLeftByArrivalAt(step));

    if (options.completesCurrent) {
      const current = this.m_currentStepName;
      this.stopStepActivity(true);
      if (
        current
        && !this.m_completedStepNames.some((n) => n.toLowerCase() === current.toLowerCase())
      ) {
        this.m_completedStepNames.push(current);
      }
      this.startStepActivity(step);
    } else {
      this.transitionStepActivity(step, false);
    }

    if (options.rewindTo === null) this.pushVisit(step.name);
    else this.rewindVisitStackTo(options.rewindTo);

    this.m_currentStepName = step.name;
    this.clearResumePointWhenReached();

    // Arm this step's Next gate BEFORE announcing the arrival — the panel
    // snapshots its view inside that emit. See `beginStepActivation`.
    const activation = this.beginStepActivation(document, step.name, isRevisit);
    this.persistWorkflowState();
    this.onRunStateChanged.emit();

    // WHO moved, announced between the state emit and the script: the state is
    // final by now, and the step's first `say` has not been sent, so a line the
    // orchestrator adds to the conversation for a learner's move lands ahead of
    // the step's own narration. See `WorkflowMoveCause` for why it is needed.
    const playableSteps = getPlayableSteps(document);
    this.onStepArrived.emit({
      stepName: step.name,
      fromStepName,
      position: Math.max(playableSteps.indexOf(step), 0) + 1,
      stepCount: playableSteps.length,
      cause: options.cause,
    });

    this.runStepActivation(document, activation);
  }

  /**
   * Stops what the step being left was still doing: the in-flight script (whose
   * revert settles with it) and Spark's current spoken line — and undoes what
   * Spark itself changed on it.
   *
   * All three, because they are different things that outlive a step change. A
   * script's `say` parks the runner for up to a minute waiting on a response
   * nothing else cancels, and a learner-asked question can be mid-answer with
   * no script running at all — so stopping only the script leaves the previous
   * step still talking over the new one. And a part Spark isolated because the
   * learner asked is in no script's ledger, so without its own undo it stayed
   * isolated through every later step and was still there when the learner
   * came back (AI-1424).
   *
   * Spark's changes are undone FIRST: they were made on top of the step's
   * authored state, so they come off before the script's revert puts that
   * state back. Every move reaches here — Next, Back, the outline, a trigger,
   * a replay, and the stop — which is why this, and not each of them, is the
   * hook. Optional-called because headless harnesses fake the runner.
   *
   * `leavingSceneId` is the scene an ARRIVAL is about to leave
   * ({@link sceneLeftByArrivalAt}); Spark's changes on it are then held until
   * it has been left (AI-1429). Every other caller passes nothing and undoes
   * everything at once.
   */
  private interruptPlayback(leavingSceneId: string | null = null): void {
    this.m_scriptRunner.revertLiveChanges?.(leavingSceneId);
    this.m_scriptRunner.stopAllRunningScripts();
    this.m_scriptRunner.interruptSpeech();
  }

  /**
   * The scene an arrival at `step` will LEAVE: the active scene, when the step
   * is bound to a different scene that exists. Null when the step stays on the
   * scene in front of the learner — no binding, the same scene, or a stale
   * reference, which plays where the learner already is
   * ({@link activateStepSceneAndStartScriptAsync} resolves it the same way).
   *
   * A prediction, not a promise: the switch can still fail or be superseded,
   * which is why the activation settles whatever this held back
   * (`settleLiveChangesAwaitingSceneLeave`).
   */
  private sceneLeftByArrivalAt(step: WorkflowStep): string | null {
    const activeSceneId = this.m_project.activeSceneId;
    const reference = step.scene?.trim() ?? "";
    if (!activeSceneId || reference.length === 0) return null;
    const target = resolveWorkflowSceneReference(this.m_project.scenes, reference);
    return target && target.id !== activeSceneId ? activeSceneId : null;
  }

  /**
   * The SYNCHRONOUS half of an arrival: claim the activation generation and arm
   * the step's Next gate. Returns what {@link runStepActivation} needs, or null
   * when there is no step to activate.
   *
   * **Split from the async half so it can run BEFORE the arrival's
   * `onRunStateChanged`**, and that ordering is the whole reason the two are
   * separate methods. That emit is the panel's only announcement of the new
   * step, and the panel SNAPSHOTS `getPlaybackView()` inside the handler rather
   * than re-reading it — so a gate armed one line after the emit is a gate the
   * panel never sees. It would render an enabled Next for the entire wait and
   * then "unblock" to the state it was already showing, which is
   * indistinguishable from the condition not working at all.
   */
  private beginStepActivation(
    document: WorkflowDocument,
    stepName: string | null,
    isRevisit: boolean,
  ): { readonly step: WorkflowStep; readonly generation: number } | null {
    // Playback-scoped like every other lookup the run makes: a step parked
    // out from under a live run behaves as a deleted one does — nothing is
    // gated, nothing is navigated to, and no script plays.
    const step = stepName ? findPlayableStep(document, stepName) : null;
    if (!step) {
      // Nothing to gate: never leave the step being LEFT holding Next on a step
      // that is not there any more. Same for the composer — a hold that
      // outlived its step would hide it with nothing left to release it.
      this.clearCompletionGate();
      this.m_aiInputHold = null;
      return null;
    }

    const generation = ++this.m_stepActivationGeneration;
    // Same emit, same reason: the viewport snapshots it there, and a hide taken
    // after the emit would leave the outgoing scene on screen for a frame
    // behind a media panel that has already expanded. Only ever SET here —
    // showing the scene again waits for a step that names one to have it built
    // ({@link showSceneOnceBuilt}), so a text step after a media step keeps the
    // viewport empty rather than bringing the old scene back.
    if (isMediaOnlyStep(step)) this.m_isSceneHidden = true;
    this.armCompletionGate(step, generation, isRevisit);
    // Withhold Spark's composer for as long as this step is doing the talking.
    // Assigned rather than released-and-armed: the step being LEFT may still
    // hold one, and its generation is now stale either way.
    //
    // Synchronously, and BEFORE the arrival's emit for the same reason the gate
    // is armed here — the panel SNAPSHOTS its view inside that emit, so a hold
    // taken one line after it is a hold the panel never sees.
    this.m_aiInputHold = step.findScriptByTrigger("on_start") ? { generation } : null;
    return { step, generation };
  }

  /**
   * The ASYNC half: a workflow part path is relative to the step's scene, so —
   * matching Unity's `TransitionToStepAsync` — activate that scene first, then
   * execute on_start.
   *
   * This IS the mechanism behind the step's `scene` field — a script never has
   * to navigate to its own step's scene, and authoring a `go_to_scene` step for
   * that is redundant (the agent-facing schema and the builder role say so).
   */
  private runStepActivation(
    document: WorkflowDocument,
    activation: { readonly step: WorkflowStep; readonly generation: number } | null,
  ): void {
    if (activation === null) return;

    const { step, generation } = activation;
    writeSceneNavigationDiagnostic("WorkflowRunModel", "step activation requested", {
      stepName: step.name,
      sceneReference: step.scene,
      generation,
      activeSceneId: this.m_project.activeSceneId,
    });
    void this.activateStepSceneAndStartScriptAsync(document, step, generation).catch((error) =>
      this.logStepActivationFailure(step.name, error));
  }

  private async activateStepSceneAndStartScriptAsync(
    document: WorkflowDocument,
    step: WorkflowStep,
    generation: number,
  ): Promise<void> {
    const sceneReference = step.scene?.trim() ?? "";
    if (sceneReference.length > 0) {
      const targetScene = resolveWorkflowSceneReference(this.m_project.scenes, sceneReference);
      if (!targetScene) {
        // A stale scene reference is a WARNING, and warnings never block a run
        // (`WORKFLOW_FILE_SPEC_documentation.md`, "Errors ARE the play gate"). Returning here
        // dropped the step's whole on_start script — narration included — so the
        // learner sat on a silent step with nothing but a console line to say
        // why, which is what made the `scene` field look inert and drove
        // `go_to_scene` script steps as a workaround.
        //
        // Degrade to the EMPTY-`scene` behaviour instead: play where the learner
        // already is. That is an already-supported, already-documented state
        // rather than a novel one, and a tool step whose part path does not
        // resolve there fails with its own visible error instead of silence.
        console.warn(
          `[WorkflowRunModel] Scene "${sceneReference}" for step "${step.name}" is not in this project; playing on_start in the current scene.`,
        );
      } else {
        if (this.m_project.activeSceneId !== targetScene.id) {
          await this.m_project.setActiveScene(
            targetScene.id,
            `workflow_step:${step.name}:generation:${generation}`,
          );
        }
        if (!this.isCurrentStepActivation(step.name, generation)) return;
        if (this.m_project.activeSceneId !== targetScene.id) {
          // Not a supersede — `isCurrentStepActivation` already covered that.
          // Someone else (the learner, or the agent) moved the scene while we
          // awaited. Same rule as above: warn, but still play.
          console.warn(
            `[WorkflowRunModel] Scene "${targetScene.name}" did not become active for step "${step.name}"; playing on_start in "${this.m_project.activeSceneId}".`,
          );
        }
        // A step that names a scene is the only thing that ends a hide (see
        // `isSceneHidden`). Only once it RESOLVED: a stale reference degrades to
        // the empty-`scene` behaviour above, and after a media step that
        // behaviour is "nothing on screen".
        this.showSceneOnceBuilt(generation);
      }
    }

    if (!this.isCurrentStepActivation(step.name, generation)) return;
    // The scene is settled, switched or not. If the move meant to leave a scene
    // and did not, Spark's changes it held back for that scene come off now —
    // on the scene still in front of the learner, which is where they are.
    // After a real switch there is nothing left to do (AI-1429).
    this.m_scriptRunner.settleLiveChangesAwaitingSceneLeave?.();
    // AFTER the scene, BEFORE the script: the step's view is its OPENING
    // framing, and a script that moves the camera itself (`focus_on_part`) is
    // meant to move it from there.
    this.applyStepView(step);
    // After this step's own switch has its rows, so the two never compete.
    this.prefetchNextStepScene(document, step);

    if (!this.isCurrentStepActivation(step.name, generation)) return;
    const script = step.findScriptByTrigger("on_start");
    if (!script) {
      // A `script_finished` gate on a step with no script would never open, so
      // it opens now. `validate()` warns about the pairing — the author is told
      // rather than the learner being walled in, which is the rule the whole
      // format follows.
      //
      // Only that kind: a `delay` gate on a step with no script is the ordinary
      // shape, and it is the CLOCK's to open, not this branch's.
      this.satisfyCompletionGate(step.name, generation, "script_finished");
      // Normally a no-op: `beginStepActivation` only takes the composer when
      // the step HAS a script, so there is nothing here to hand back. Called
      // anyway because the one thing this latch must never do is outlive a
      // branch that plays nothing — a hidden composer has no other way back.
      this.releaseAiInputHold(generation);
      return;
    }

    try {
      const result = await this.m_scriptRunner.playScriptAsync(document, step, script);
      if (result.startsWith("error:")) {
        console.warn(`[WorkflowRunModel] On-start script for "${step.name}" failed: ${result}`);
      }
    } finally {
      // ANY terminal outcome opens the gate — finished, stopped, or failed. A
      // script that errored has still stopped happening, and a learner held on
      // a step because a tool call went wrong has no way to know or fix it.
      // (`finally`, so a throw the caller logs opens it too.)
      //
      // A `delay` gate is NOT this branch's to open, even though its script
      // settling is the last thing to happen on most steps: the author asked
      // for a wall-clock wait from arrival, and a script that finished in
      // 200 ms must not shorten it to 200 ms.
      this.satisfyCompletionGate(step.name, generation, "script_finished");
      // …and the composer comes back on the SAME terminal outcomes, for the
      // same reason: a learner left with no way to ask a question because a
      // tool call went wrong has no way to know or fix it.
      this.releaseAiInputHold(generation);
    }
  }

  /**
   * Fetches the NEXT playable step's scene rows while this step plays, so a
   * Next press does not spend the switch's data phase before the step can
   * speak — ~0.7 s on a first visit (measured 2026-09-30), all of it ahead of
   * the narration because the script waits for its scene.
   *
   * Data only: no activation and no residency, so a step whose models still
   * have to download keeps its loading screen (`prefetchSceneDataAsync`). Only
   * the next step, because that is where a learner goes from here almost every
   * time, and one scene's rows is the whole cost of a wrong guess.
   */
  private prefetchNextStepScene(document: WorkflowDocument, step: WorkflowStep): void {
    const playableSteps = getPlayableSteps(document);
    const next = playableSteps[playableSteps.indexOf(step) + 1];
    const reference = next?.scene?.trim() ?? "";
    if (reference.length === 0) return;
    const target = resolveWorkflowSceneReference(this.m_project.scenes, reference);
    if (!target || target.id === this.m_project.activeSceneId) return;
    void this.m_project.prefetchSceneDataAsync(target.id).catch((error: unknown) => {
      console.warn(`[WorkflowRunModel] Prefetch of scene "${target.name}" failed:`, error);
    });
  }

  // ---- The Next gate (`completion_condition`) --------------------

  /**
   * Whether Next is held back on this step right now, and why.
   *
   * **Fails open on anything it cannot vouch for**, and every branch here is a
   * real state rather than defensive padding: no gate armed (the run has not
   * arrived yet), a gate belonging to another step, and a gate whose KIND no
   * longer matches the document — which is what a builder edit mid-run looks
   * like, since the gate was armed from the document as it stood when the
   * learner arrived. In all three the condition applies from the NEXT arrival,
   * not retroactively, and the alternative would be a Next button that can
   * never be pressed with nothing on screen explaining it.
   *
   * The reason is written to be read by a person AND handed to the agent, so
   * it says what is being waited for rather than naming the key.
   */
  private describeNextGate(step: WorkflowStep): {
    readonly isBlocked: boolean;
    readonly reason: string | null;
  } {
    const condition = resolveStepCompletionCondition(step);
    if (condition === null) return { isBlocked: false, reason: null };

    const gate = this.m_completionGate;
    if (gate === null || gate.satisfied) return { isBlocked: false, reason: null };
    if (gate.stepKey !== step.name.toLowerCase()) return { isBlocked: false, reason: null };
    if (gate.kind !== condition.kind) return { isBlocked: false, reason: null };

    return { isBlocked: true, reason: describeGateReason(condition) };
  }

  /**
   * The POSITIVE half of {@link describeNextGate}: this step HAD a condition
   * and it has since been met on this arrival, so the wait the learner was
   * told about is over.
   *
   * A separate query rather than a third field on the gate description because
   * the two answer different questions and only one of them has a UI: the
   * panel needs "is Next disabled", and only the prompt needs "was there a
   * wait, and is it done". The bullet it feeds exists because the ABSENCE of
   * the blocked bullet is not a statement — the agent that said "we're waiting
   * for the video" last turn has nothing to contradict it with, and goes on
   * saying it (see `buildRuntimePromptInstructions`).
   *
   * Returns null for a step with no condition and for one whose wait is still
   * running, so a step that was never gated says nothing at all.
   */
  private describeSettledGate(step: WorkflowStep): string | null {
    const condition = resolveStepCompletionCondition(step);
    if (condition === null) return null;

    const gate = this.m_completionGate;
    // A REVISIT arms no gate at all (`armCompletionGate`), so there is nothing
    // to announce as finished — Next was available from the moment they
    // arrived and nothing ever said otherwise.
    if (gate === null || !gate.satisfied) return null;
    if (gate.stepKey !== step.name.toLowerCase()) return null;
    if (gate.kind !== condition.kind) return null;

    return describeSettledGateReason(condition);
  }

  /**
   * Opens a gate for the step just arrived at, if it has a condition.
   *
   * The clock starts on ARRIVAL, not when the step's scene finishes loading or
   * its script starts speaking: the play panel is already showing the step by
   * then, so arrival is the moment the learner starts reading it.
   */
  private armCompletionGate(
    step: WorkflowStep,
    generation: number,
    isRevisit: boolean,
  ): void {
    this.clearCompletionGate();
    // A REVISIT is never gated: the condition says what has to happen before
    // the learner may leave this step for the FIRST time, and covered ground
    // has already been covered. Charging the wait again on the way back turns
    // a "read this before moving on" into a toll on every pass — worst on a
    // step a trigger loops through, where it would be paid every time.
    //
    // Deliberately keyed on `visited` (arrived at) rather than `completed`
    // (left via Next): a step reached by one trigger branch and left by
    // another never completes, so a `completed` test would re-gate it forever.
    // The one case they differ on is a step the learner arrived at and left
    // before the wait elapsed — that counts as covered here.
    //
    // Note this must be answered from BEFORE the arrival: `pushVisit` runs
    // earlier in `arriveAtStep`, so by now the step is always in the list.
    // Hence the caller passes it rather than this asking `hasVisitedStep`.
    //
    // After `clearCompletionGate`, never before it: the step being LEFT must
    // not keep holding its own gate open on the step now current.
    if (isRevisit) return;
    const condition = resolveStepCompletionCondition(step);
    if (condition === null) return;

    // A `video_finished` gate on a step that shows no video would never open —
    // nothing renders a `<video>`, so nothing can report it finished. It is
    // simply NOT ARMED, which `describeNextGate` reads as "Next is available",
    // and `validate()` warns the author instead. (`script_finished` reaches the
    // same outcome by arming and then satisfying itself in
    // `activateStepSceneAndStartScriptAsync`, where it has to look for the
    // script anyway; here the answer is knowable synchronously, and not arming
    // avoids emitting a state change from inside `beginStepActivation` — which
    // runs BEFORE the arrival's own emit.)
    if (condition.kind === "video_finished" && !stepHasPlayableVideo(step)) return;

    this.m_completionGate = {
      stepKey: step.name.toLowerCase(),
      generation,
      kind: condition.kind,
      satisfied: false,
    };

    // A `script_finished` gate is opened by the script settling, in
    // `activateStepSceneAndStartScriptAsync`; only a delay needs a clock.
    if (condition.kind !== "delay") return;
    this.m_completionGateTimer = setTimeout(() => {
      this.m_completionGateTimer = null;
      this.satisfyCompletionGate(step.name, generation, "delay");
    }, condition.delaySeconds * 1000);
  }

  /**
   * Opens the gate, if the caller is still talking about the arrival that armed
   * it AND is discharging the condition that gate is actually waiting on.
   *
   * Three guards, each load-bearing. The generation check: a script settles
   * long after the learner may have navigated away, and satisfying blindly
   * would unlock whichever step they are on now. The step check, likewise, for
   * a same-generation mismatch.
   *
   * And the KIND check, which is why every caller has to name the condition it
   * is discharging rather than just saying "open it". A step's on-start script
   * settles — or turns out not to exist — on every arrival, gate or no gate, so
   * a kind-blind open let the script path discharge a `delay` gate the instant
   * the step arrived: the timer still ran, found the gate already satisfied and
   * did nothing, and a `delay` on a step with no script (the common shape) held
   * Next back for exactly zero seconds.
   */
  private satisfyCompletionGate(
    stepName: string,
    generation: number,
    kind: WorkflowCompletionConditionType,
  ): void {
    const gate = this.m_completionGate;
    if (gate === null || gate.satisfied) return;
    if (gate.generation !== generation) return;
    if (gate.stepKey !== stepName.toLowerCase()) return;
    if (gate.kind !== kind) return;

    gate.satisfied = true;
    // The panel re-derives its whole view from this, which is how the Next
    // button stops being disabled without anything polling.
    this.onRunStateChanged.emit();
  }

  /**
   * Hands Spark's composer back, if the caller is still talking about the
   * arrival that took it.
   *
   * Idempotent and one-way: once released, only the NEXT arrival can take it
   * again. Emits only on a real change, so the routine "already released" call
   * from a step with no script costs nothing.
   */
  private releaseAiInputHold(generation: number): void {
    if (this.m_aiInputHold === null || this.m_aiInputHold.generation !== generation) return;
    this.m_aiInputHold = null;
    // The panel re-derives its whole view from this, which is how the composer
    // reappears without anything polling.
    this.onRunStateChanged.emit();
  }

  /**
   * The step's media video reached its end — the seam behind a
   * `video_finished` condition.
   *
   * Pushed IN by the UI, the direction `IProjectModel.reportSceneBuild*`
   * already establishes: the `<video>` lives in `WorkflowMediaView` and this
   * model must never import a renderer, but it is the model that owns whether
   * the learner may move on. A run with no media panel mounted simply never
   * gets a report, which is why the gate is not armed for a step that shows no
   * video in the first place.
   *
   * "Finished" is ANY terminal outcome, exactly as it is for `script_finished`:
   * the clip ran out, the authored trim's end was reached, or the media could
   * not be loaded at all. A learner held on a step because an S3 object was
   * deleted has no way to know or fix it, and the format's rule is that a
   * broken reference is work for the builder, never a wall for the learner.
   *
   * Guarded by the step NAME and by the live activation generation, so a report
   * from an element belonging to a step the learner has already left cannot
   * open the gate on the step they are on now. Repeat reports are free — the
   * gate only opens once.
   */
  reportStepMediaFinished(stepName: string): void {
    this.satisfyCompletionGate(stepName, this.m_stepActivationGeneration, "video_finished");
  }

  attachMediaPlaybackHost(host: IWorkflowMediaPlaybackHost): () => void {
    this.m_mediaPlaybackHost = host;
    return () => {
      // Only ours. A panel that has already been replaced must not clear its
      // successor's registration on the way out.
      if (this.m_mediaPlaybackHost === host) this.m_mediaPlaybackHost = null;
    };
  }

  /**
   * `control_media` — replay, play, pause, or skip the current step's video.
   *
   * The order of the refusals is what makes each reply useful: the three the
   * MODEL can answer are permanent facts about where the run is (nothing
   * playing, a step that shows nothing, a step that shows a picture), while the
   * host's is transient (the file is still fetching, or would not load). Asking
   * the host first would report a fetching video for a step that has none.
   *
   * The current step's media is read from {@link getPlaybackView}, not from the
   * step's raw `media` block, so this and the panel can never disagree about
   * what is on screen — `resolvePlaybackMedia` is where a block with no url
   * becomes "no media" and where the kind is inferred.
   */
  controlStepMedia(command: WorkflowMediaCommand): WorkflowMediaControlResult {
    // Null covers both "no run" and the anomaly of a run whose current step is
    // gone (a mid-run edit can park or delete it), which come to the same thing
    // here: there is no step to have a video. The executor's wording says so
    // rather than asserting the first reading.
    const view = this.getPlaybackView();
    if (view === null) return { outcome: "not_running" };
    if (view.media === null) return { outcome: "no_media" };
    if (view.media.kind !== "video") return { outcome: "not_video" };

    const host = this.m_mediaPlaybackHost;
    if (host === null) return { outcome: "unavailable" };

    const state = host.controlMedia(command);
    return state === null ? { outcome: "unavailable" } : { outcome: "ok", state };
  }

  attachOutlineHost(host: IWorkflowOutlineHost): () => void {
    this.m_outlineHost = host;
    return () => {
      // Only ours — same identity guard, same reason, as the media host above.
      if (this.m_outlineHost === host) this.m_outlineHost = null;
    };
  }

  /**
   * `Toggle_Workflow_Outline` — show or hide the outline panel.
   *
   * ⚠ Deliberately NOT gated on {@link isRunning}, unlike every other member
   * around it: the outline is the workflow editor's panel as much as the run's,
   * and one flag drives both. What it IS gated on is having something to show,
   * which the host answers — a workflow with no steps has an empty outline, and
   * opening it would be a panel with nothing in it.
   *
   * The state is read back AFTER the write rather than predicted, because the
   * host's flag is set synchronously — nothing here has to guess.
   */
  controlOutline(visible: boolean): WorkflowOutlineState | null {
    const host = this.m_outlineHost;
    if (host === null) return null;
    if (visible && host.getOutlineState().entryCount === 0) return host.getOutlineState();
    host.setOutlineVisible(visible);
    return host.getOutlineState();
  }

  private clearCompletionGate(): void {
    if (this.m_completionGateTimer !== null) {
      clearTimeout(this.m_completionGateTimer);
      this.m_completionGateTimer = null;
    }
    this.m_completionGate = null;
  }

  /**
   * Flies the camera to the step's authored framing — its `step_view`, or,
   * when it has none, its scene's saved view.
   *
   * This is the mechanism behind `step_view`, and it is deliberately the SAME
   * move a scene change makes: same pose pair (camera position + orbit pivot,
   * Unity world space), same duration table
   * ({@link cameraTransitionDurationSeconds}), same seam. A step view is a
   * scene view one level down.
   *
   * Nothing is persisted — `applyTransientCameraView` writes no scene row and
   * broadcasts nothing. The framing lives in the workflow document; the scene's
   * own view is untouched, so Reset still returns the learner to the SCENE's
   * framing rather than to the step's, which is what Reset should mean.
   *
   * **Every step of a run opens on a view** (AI-1424, user direction
   * 2026-09-29: "every step should bring it back to the scene view that was
   * set for that step's scene"). A step with no view — or a half-written one —
   * falls back to its scene's saved view, even when the learner or Spark moved
   * the camera: the scene change's own framing skips a moved camera
   * (`SceneModel.setSceneCamera`), and a step in the SAME scene has no scene
   * change at all, so before this a step landed wherever the previous one left
   * the camera. `frameActiveSceneView` does not re-fly a camera already on its
   * way there, so the common case — a scene change already heading to that
   * view — is one move, not two.
   *
   * Run-only by construction: this is reached from a run's step activation and
   * nowhere else, so an author BUILDING the workflow keeps their camera. A run
   * with no viewport leaves it alone.
   *
   * ⚠ Known interaction, pre-existing in shape: a script declaring `revert`
   * captures the camera when its first tool step runs, which is while this move
   * is still in the air — so the revert returns the camera to where the step
   * BEGAN rather than to the step's view. A scene change's own fly has the same
   * interaction with the same capture; the fix belongs with that capture (it
   * should record a transition's DESTINATION, not its current pose), not here.
   */
  private applyStepView(step: WorkflowStep): void {
    const scene = this.m_resolveSceneModel();
    if (!scene) return;

    const view = resolveStepView(step);
    if (view === null) {
      const applied = scene.frameActiveSceneView();
      writeSceneNavigationDiagnostic("WorkflowRunModel", "scene view applied for step", {
        stepName: step.name,
        applied,
      });
      return;
    }

    // Unity's own fallback when there is no active scene to read a speed from
    // (`DesktopSceneModel.cs:228`): Quick.
    const speed = this.m_project.activeScene?.transitionSpeed ?? 1;
    const applied = scene.applyTransientCameraView(
      { position: view.position, pivot: view.pivot },
      cameraTransitionDurationSeconds(speed),
    );
    writeSceneNavigationDiagnostic("WorkflowRunModel", "step view applied", {
      stepName: step.name,
      applied,
      position: view.position,
      pivot: view.pivot,
    });
  }

  /**
   * Ends a hide ({@link IWorkflowRunModel.isSceneHidden}) once the scene now
   * active is on screen: at once when it already is, or when its load session
   * settles ({@link settleSceneLoad}) when it is still being built.
   *
   * The session is the signal because the renderer closes it in the same tick
   * it puts the scene on screen. A scene that was already resident opens no
   * session at all, and the renderer swaps it in synchronously inside the
   * `onActiveSceneChanged` emit that `setActiveScene` has already returned
   * from — so by the time this runs, there is nothing left to wait for.
   *
   * A no-op for a stale `generation`: the learner has moved on, and the step
   * they moved to decides for itself.
   */
  private showSceneOnceBuilt(generation: number): void {
    if (!this.m_isSceneHidden || generation !== this.m_stepActivationGeneration) return;
    const activeSceneId = this.m_project.activeSceneId;
    if (activeSceneId !== null && this.m_loadingSceneId === activeSceneId) {
      this.m_pendingSceneReveal = { sceneId: activeSceneId, generation };
      return;
    }
    this.showHiddenScene();
  }

  /** The other half of every hide. Emits, because nothing else is announcing it. */
  private showHiddenScene(): void {
    this.m_pendingSceneReveal = null;
    if (!this.m_isSceneHidden) return;
    this.m_isSceneHidden = false;
    writeSceneNavigationDiagnostic("WorkflowRunModel", "hidden scene shown", {
      activeSceneId: this.m_project.activeSceneId,
      currentStepName: this.m_currentStepName,
      isRunning: this.m_isRunning,
    });
    this.onRunStateChanged.emit();
  }

  private isCurrentStepActivation(stepName: string, generation: number): boolean {
    return (
      this.m_isRunning
      && generation === this.m_stepActivationGeneration
      && this.m_currentStepName?.toLowerCase() === stepName.toLowerCase()
    );
  }

  private logStepActivationFailure(stepName: string, error: unknown): void {
    console.warn(
      `[WorkflowRunModel] Could not activate scene for step "${stepName}":`,
      error,
    );
  }

  /**
   * Restores current / completed / visited / resume from this user's DynamoDB
   * row.
   *
   * **Only when the workflow asks to be remembered** (`remember_progress`,
   * absent = false). Otherwise a starting run keeps the cleared state above, so
   * the learner begins at the first step having seen nothing — a training
   * workflow is normally taken from the top, and the state on disk is left to be
   * overwritten by this run rather than read.
   *
   * Every restored name is re-validated against the document's steps AND
   * stored in the STEP's canonical casing, not the state's: the
   * completion-advancement gate compares `state.stepName` (always canonical)
   * against the current name with `!==`, so a case-drifted restore would
   * stall the run on its first step. A state left behind by a since-renamed
   * or deleted step is dropped rather than resuming onto a step that no
   * longer exists.
   */
  private loadRunState(
    document: WorkflowDocument,
    state: WorkflowRunState | null,
  ): void {
    this.m_completedStepNames.length = 0;
    this.m_visitedStepNames.length = 0;
    this.m_visitStack.length = 0;
    this.m_currentStepName = null;
    this.m_resumePointStepName = null;

    if (!state || !isProgressRemembered(document)) return;

    // Every lookup here is playback-scoped, so a step parked since the last run
    // is dropped from the restored progress exactly as a deleted one is — the
    // learner is not resumed onto, or counted against, a step that no longer
    // plays.
    const currentStep = findPlayableStep(document, state.current ?? "");
    if (currentStep) {
      this.m_currentStepName = currentStep.name;
    }

    for (const name of state.completed) {
      const step = findPlayableStep(document, name);
      if (step) this.m_completedStepNames.push(step.name);
    }

    const resumeStep = findPlayableStep(document, state.resumePoint ?? "");
    if (resumeStep) {
      this.m_resumePointStepName = resumeStep.name;
    }

    // Resume from the saved point when the run restarts (Unity resume-point flow).
    if (!this.m_currentStepName && this.m_resumePointStepName) {
      this.m_currentStepName = this.m_resumePointStepName;
    }

    // The path restores FIRST and keeps its duplicates — it is a route, not a
    // set — dropping only entries whose step the document no longer has. A
    // rename mid-workflow shortens the route rather than resuming onto a step
    // that is not there.
    for (const name of state.visitStack) {
      const step = findPlayableStep(document, name);
      if (step) this.m_visitStack.push(step.name);
    }

    // A completed step was necessarily seen, so the completed list is folded in
    // rather than trusted to be a subset: a file written before `visited`
    // existed still restores usable covered ground instead of none. The path is
    // folded in for the same reason from the other side — a row that has the
    // route but not the set still restores where they have been.
    for (const name of [...state.visited, ...state.completed, ...this.m_visitStack]) {
      this.markStepVisited(findPlayableStep(document, name)?.name ?? "");
    }
    this.markStepVisited(this.m_currentStepName ?? "");
  }

  /**
   * Records that the run has LANDED on a step (canonical casing, ignoring
   * unknown names). One seam for every arrival — start, jump, Back, Next,
   * trigger — so no navigation path can add a step to the run without the
   * covered ground learning about it.
   */
  private markStepVisited(stepName: string): void {
    const name = stepName.trim();
    if (name.length === 0) return;
    if (this.m_visitedStepNames.some((visited) => visited.toLowerCase() === name.toLowerCase())) {
      return;
    }
    this.m_visitedStepNames.push(name);
  }

  /** Extends the path by one arrival, and the covered ground with it. */
  private pushVisit(stepName: string): void {
    const name = stepName.trim();
    if (name.length === 0) return;
    this.m_visitStack.push(name);
    this.markStepVisited(name);
  }

  /**
   * Drops every arrival after `index`, so the path once again ends where the
   * learner now is.
   *
   * The deepest dropped entry — the step they were on — becomes the RESUME
   * POINT, but only when there is not one already: a second "back one" is
   * still the same interruption, and letting it overwrite would walk the
   * "where you were" marker backwards one step at a time until it named
   * somewhere they never actually left off. It is cleared by arriving there
   * (see {@link arriveAtStep}), which re-arms it for the next detour.
   */
  /**
   * Drops the resume point once the run is standing on it — there is nothing
   * left to offer a way back to, and it re-arms for the next detour.
   *
   * Called on every arrival AND on start, because `stop()` records the step the
   * run stopped on: a remembered run resumes onto exactly that step, and
   * leaving the marker set would have the Guide offering to return the learner
   * to where they already are on their first turn.
   */
  private clearResumePointWhenReached(): void {
    const resume = this.m_resumePointStepName;
    const current = this.m_currentStepName;
    if (!resume || !current) return;
    if (resume.toLowerCase() === current.toLowerCase()) {
      this.m_resumePointStepName = null;
    }
  }

  private rewindVisitStackTo(index: number): void {
    if (index < 0 || index >= this.m_visitStack.length) return;

    const dropped = this.m_visitStack.slice(index + 1);
    if (dropped.length > 0 && !this.m_resumePointStepName) {
      this.m_resumePointStepName = dropped[dropped.length - 1];
    }
    this.m_visitStack.length = index + 1;
  }

  /** Index of the learner's most recent arrival on a step, or -1. */
  private findLastVisitIndex(stepName: string): number {
    const name = stepName.trim().toLowerCase();
    if (name.length === 0) return -1;
    for (let i = this.m_visitStack.length - 1; i >= 0; i--) {
      if (this.m_visitStack[i].toLowerCase() === name) return i;
    }
    return -1;
  }

  /**
   * The path must END on the step the run is on — that invariant is what makes
   * `length - 1` the number of steps the learner can be taken back.
   *
   * Called once, when a run starts: the restored path may end somewhere else
   * (`current` was resolved from the resume point), be empty (a row written
   * before the attribute existed, or a run that does not remember progress), or
   * name a step the document no longer has.
   */
  private syncVisitStackToCurrentStep(): void {
    const current = this.m_currentStepName;
    if (!current) return;

    const top = this.m_visitStack[this.m_visitStack.length - 1] ?? null;
    if (top !== null && top.toLowerCase() === current.toLowerCase()) {
      this.markStepVisited(current);
      return;
    }
    this.pushVisit(current);
  }

  private persistWorkflowState(): void {
    const document = this.m_workflowModel.loadedWorkflow;
    if (!document) return;

    const state = this.buildWorkflowState(document);
    this.m_persistedState = state;
    void this.m_workflowModel.updateWorkflowStateAsync(state).catch((error) => {
      console.warn("[WorkflowRunModel] Failed to persist workflow user state:", error);
    });
  }

  private buildWorkflowState(document: WorkflowDocument): WorkflowRunState {
    // The persisted position and count describe the RUN, so they are over the
    // playable steps — the same numbers the panel and the guide are given.
    const steps = getPlayableSteps(document);
    const stepIndex = this.m_currentStepName
      ? steps.findIndex((t) => t.name.toLowerCase() === this.m_currentStepName!.toLowerCase())
      : -1;
    const currentGroupName = stepIndex >= 0 ? steps[stepIndex].groupName : "";

    return {
      run: this.m_isRunning ? "in progress" : "not running",
      current: this.m_currentStepName,
      // The containing group's name, or null for an ungrouped step.
      currentGroup: currentGroupName.length > 0 ? currentGroupName : null,
      // 1-based position, kept as its own field instead of the old
      // "(step 1 of 3)" suffix the reader then had to strip back off.
      currentIndex: stepIndex >= 0 ? stepIndex + 1 : null,
      stepCount: steps.length,
      completed: this.m_completedStepNames.slice(),
      visited: this.m_visitedStepNames.slice(),
      visitStack: this.m_visitStack.slice(),
      resumePoint: this.m_resumePointStepName,
      // Prose the guide agent acts on. The stopped case has to follow
      // `remember_progress`: promising a resume that the next start will discard
      // is exactly the kind of lie the state exists to prevent.
      next: this.m_isRunning
        ? "wait for the learner to press Next (or a trigger button); Next completes the step and the run advances"
        : isProgressRemembered(document)
          ? "offer to continue from the resume point when play mode starts"
          : "this workflow does not remember progress; the next run starts from the first step",
    };
  }

  private async prepareUserStateAsync(document: WorkflowDocument): Promise<void> {
    const key = this.userStateKey(document);
    // Progress is always written for telemetry, but only restored when the
    // workflow explicitly opts into resume. A stale row must not block a run
    // that starts fresh, which also matches Unity's start path.
    if (!isProgressRemembered(document) || !key) {
      this.clearLoadedUserState();
      this.m_loadedStateKey = "memory-only";
      return;
    }

    if (this.m_loadedStateKey === key) return;
    if (this.m_stateLoadKey === key && this.m_stateLoadPromise) {
      await this.m_stateLoadPromise;
      return;
    }

    const generation = ++this.m_stateGeneration;
    this.m_loadedStateKey = null;
    this.m_persistedState = null;
    this.m_stateLoadKey = key;
    const load = this.m_workflowModel.getWorkflowStateAsync().then((state) => {
      if (generation !== this.m_stateGeneration) return;
      this.m_persistedState = state;
      this.m_loadedStateKey = key;
    });
    this.m_stateLoadPromise = load;
    try {
      await load;
    } finally {
      if (this.m_stateLoadPromise === load) {
        this.m_stateLoadPromise = null;
        this.m_stateLoadKey = null;
      }
    }
  }

  private userStateKey(document: WorkflowDocument): string | null {
    const user = this.m_activity?.auth.currentUser;
    const accountId = user?.customerId?.trim() ?? "";
    const userId = user?.userId?.trim() ?? "";
    const projectId = document.projectId.trim();
    const workflowId = document.uid.trim();
    if (!accountId || !userId || !projectId || !workflowId) return null;
    return [accountId, userId, projectId, workflowId].join("\u001f");
  }

  private clearLoadedUserState(): void {
    this.m_stateGeneration += 1;
    this.m_loadedStateKey = null;
    this.m_stateLoadKey = null;
    this.m_stateLoadPromise = null;
    this.m_persistedState = null;
  }

  private resetRunState(): void {
    this.m_startGeneration += 1;
    // The document this was parked against is going away (project unload,
    // delete-eviction, import, sign-out). The dwell re-check would drop it
    // anyway on identity, but a live timer over a torn-down run is not a state
    // worth leaving reachable.
    this.cancelPendingAutostart();
    this.cancelConsolePlacementWait();
    const wasRunning = this.m_isRunning;
    if (wasRunning) {
      this.stopWorkflowActivity(this.m_activeActivity?.isComplete ?? false);
    }
    this.m_isRunning = false;
    this.m_stepActivationGeneration++;
    // The step being torn down takes its Next gate with it — a surviving timer
    // would fire against a run that no longer exists — and its hold on the
    // composer with it.
    this.clearCompletionGate();
    this.m_aiInputHold = null;
    // The scene hide goes too, whatever was waiting on it: a viewer's project
    // closes right after their run ends, and a hide carried into the next
    // project would open it on an empty viewport with no step to end it.
    const wasSceneHidden = this.m_isSceneHidden;
    this.m_isSceneHidden = false;
    this.m_pendingSceneReveal = null;
    this.m_currentStepName = null;
    this.m_resumePointStepName = null;
    this.m_completedStepNames.length = 0;
    this.m_visitedStepNames.length = 0;
    this.m_visitStack.length = 0;

    // The document going away mid-run (project unload, delete-eviction) must
    // tear the run down like stop() does: an in-flight script would otherwise
    // keep narrating and dispatching tool steps against the projects grid or
    // the next project, and the play-mode flag would stay latched — swallowing
    // the next project's first Preview press in the setter's dedup.
    if (wasRunning) {
      this.interruptPlayback();
      this.finishPlaybackKeepingDelegation();
      if (this.m_project.isSceneStartPlayModeEnabled) {
        this.m_project.isSceneStartPlayModeEnabled = false;
      }

      this.onRunStateChanged.emit();
    } else if (wasSceneHidden) {
      // A stop's return to the first scene was still in flight. Nothing else
      // announces the hide ending here, and the viewport is watching for it.
      this.onRunStateChanged.emit();
    }
    this.syncPresenterDelegation();
  }
}

/**
 * {@link WorkflowPlaybackView.isMediaOnly}: the step names no scene and carries
 * media {@link resolvePlaybackMedia} would show. The scene term reads the same
 * trimmed reference `activateStepSceneAndStartScriptAsync` navigates on, so
 * "this step names no scene" means one thing to the panel and to the run — a
 * whitespace-only value is no scene either. The media term is
 * `resolvePlaybackMedia`'s own two refusals, a missing block and an empty url.
 */
function isMediaOnlyStep(step: WorkflowStep): boolean {
  if ((step.scene?.trim() ?? "").length > 0) return false;
  return step.media !== null && step.media.url.trim().length > 0;
}

/**
 * The step's media as the panel consumes it: kind decided, timecodes in
 * seconds, or null when the step has no media to show.
 *
 * A media block with no url resolves to NULL rather than to an empty panel —
 * the panel's whole job is to show something, and a frame around nothing reads
 * as a broken workflow. `validate()` already reports the missing url, so the
 * author is not left guessing why nothing appeared.
 *
 * An unparseable timecode also resolves to null (no trim) rather than to 0:
 * `validate()` errors on it, and starting a five-minute video from the
 * beginning is a far smaller surprise than trimming it to nothing.
 */
function resolvePlaybackMedia(
  step: WorkflowStep,
  tokens: WorkflowTextTokenContext,
): WorkflowPlaybackMedia | null {
  const media = step.media;
  if (media === null) return null;

  const url = media.url.trim();
  if (url.length === 0) return null;

  const kind = resolveMediaType(media);
  const isVideo = kind === "video";
  return {
    kind,
    fit: resolveMediaFit(media),
    // The URL is an ADDRESS, so it is never token-substituted: the three text
    // fields below are what a learner reads.
    url,
    name: resolveWorkflowText(media.name.trim(), tokens),
    description: resolveWorkflowText(media.description.trim(), tokens),
    transcript: isVideo ? resolveWorkflowText(media.transcript.trim(), tokens) : "",
    startSeconds: isVideo ? parseMediaTimecodeSeconds(media.startTime) : null,
    endSeconds: isVideo ? parseMediaTimecodeSeconds(media.endTime) : null,
    // Scoped to a video like the three above it: looping an image means
    // nothing, and `validate()` warns when one carries the key.
    loop: isVideo && resolveMediaLoop(media),
    autoplay: isVideo && resolveMediaAutoplay(media),
  };
}

/**
 * {@link resolveWorkflowText} over a label that may be absent. Null is the
 * author having written NO label, which each button reads its own way (Back
 * draws its arrow alone, Next is not drawn at all) — so it has to survive the
 * substitution as null rather than becoming an empty string.
 */
function resolveNullableText(
  text: string | null,
  tokens: WorkflowTextTokenContext,
): string | null {
  return text === null ? null : resolveWorkflowText(text, tokens);
}

/**
 * The scene-navigation log's `reason` for an arrival — the phrases every
 * caller used to pass as free text, so the log still reads the same. Exhaustive
 * over {@link WorkflowMoveCause}, so a new mover is a compile error here rather
 * than an unlabelled line in the log.
 */
function describeMoveCause(cause: WorkflowMoveCause): string {
  if (cause.by === "learner") {
    switch (cause.via) {
      case "next":
        return "learner pressed Next (completed the step)";
      case "back":
        return "learner pressed Back";
      case "outline":
        return "learner picked it from the outline";
      case "trigger":
        return `trigger button "${cause.label}"`;
    }
  }
  switch (cause.via) {
    case "next":
      return "the agent advanced the run (completed the step)";
    case "back":
      return "the agent took the learner back";
    case "step":
      return "the agent moved the learner there";
    case "replay":
      return "the agent replayed the current step";
    // Distinguished from the learner's press in the log because the two are
    // different events to read back: the learner clicked the button, or they
    // said it and the Guide pressed it for them.
    case "trigger":
      return `the agent pressed trigger button "${cause.label}" for the learner`;
  }
}

/**
 * Why Next is held back, in a phrase written to be read by a person AND handed
 * to the agent verbatim (`go_to_workflow_step` quotes it in `step_not_ready`).
 *
 * It names what is being WAITED FOR rather than the key that asked for it —
 * "this step's video has not finished yet" is actionable where
 * "completion_condition: video_finished" is not. Exhaustive over the enum, so
 * a fourth kind is a compile error here rather than a blank reason at runtime.
 */
function describeGateReason(condition: {
  readonly kind: WorkflowCompletionConditionType;
  readonly delaySeconds: number;
}): string {
  switch (condition.kind) {
    case "delay":
      return `this step waits ${condition.delaySeconds}s before the learner can move on`;
    case "script_finished":
      return "this step's script is still playing";
    case "video_finished":
      return "this step's video has not finished playing yet";
  }
}

/**
 * The same wait, in the past tense — what {@link describeGateReason} was
 * waiting for, now over.
 *
 * Its own function rather than a tense flag on the one above, because the two
 * have different jobs: that one is read by a PERSON on a disabled button as
 * well as by the agent, and this one is only ever a fact handed to the agent.
 * Exhaustive over the enum for the same reason — a fourth kind is a compile
 * error here rather than a blank statement at runtime.
 */
function describeSettledGateReason(condition: {
  readonly kind: WorkflowCompletionConditionType;
  readonly delaySeconds: number;
}): string {
  switch (condition.kind) {
    case "delay":
      return `its ${condition.delaySeconds}s wait has elapsed`;
    case "script_finished":
      return "its script has finished playing";
    case "video_finished":
      return "its video has finished playing";
  }
}
